import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import { performRealMatch } from './geminiService';
import { mapBullhornSubmissionStatusToLocalStage } from './bullhornStatusMap';
import {
  filterPortalVisibleVacancies,
  isPortalTalentPoolAllowedApplicationStatus,
  sanitizeVacancyIdsForInFilter,
  type PortalPrivacyOptions,
} from '../utils/clientPortalGuards';
import {
    buildVoorgesteldCandidateNoteContent,
    voorgesteldCandidateNoteMarker,
    type VoorgesteldClientPayload,
} from '../utils/portalProposalThread';
import { WERFTOOLS_DEFAULT_TENANT_CLIENT_ID } from '../constants';
import { searchDbIlikePattern, searchTextIncludesAny } from '../utils/searchText';
import {
    companyNameFromVacancyRecord,
    vacancyTitleFromVacancyRecord,
    type CandidateWhatsAppVacancyContext,
} from '../utils/whatsappShortcodes';
import { applicationStatusImpliesPlacement } from '../utils/candidateJourneyBuckets';
import { normalizeCandidateSource, resolveApplicantCandidateSource } from '../utils/candidateSource';
import type { BullhornMappingEntity, FieldMappingRule } from './bullhornFieldMappings';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const chunkArray = <T>(arr: T[], size: number): T[][] => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
        arr.slice(i * size, i * size + size)
    );
};

const bullhornMappingCache = new Map<string, { at: number; data: Partial<Record<BullhornMappingEntity, FieldMappingRule[]>> }>();
const BULLHORN_MAPPING_CACHE_MS = 60_000;

const getByPath = (obj: any, path: string): any => {
    if (!obj || !path) return undefined;
    return String(path)
        .split('.')
        .reduce((acc: any, part: string) => (acc == null ? undefined : acc[part]), obj);
};

const getInboundRules = (rules: FieldMappingRule[] | undefined, werftoolsField: string): FieldMappingRule[] => {
    return (rules || []).filter((r) =>
        r.werftoolsField === werftoolsField &&
        (r.direction === 'inbound' || r.direction === 'both')
    );
};

const getMappedValue = (bhData: any, rules: FieldMappingRule[] | undefined, werftoolsField: string): any => {
    const fieldRules = getInboundRules(rules, werftoolsField);
    for (const r of fieldRules) {
        const v = getByPath(bhData, r.bullhornField);
        if (v !== undefined && v !== null && `${v}` !== '') return v;
    }
    return undefined;
};

const getMappedValues = (bhData: any, rules: FieldMappingRule[] | undefined, werftoolsField: string): any[] => {
    const vals: any[] = [];
    for (const r of getInboundRules(rules, werftoolsField)) {
        const v = getByPath(bhData, r.bullhornField);
        if (v !== undefined && v !== null && `${v}` !== '') vals.push(v);
    }
    return vals;
};

const getBullhornEntityMappings = async (clientId: string, entity: BullhornMappingEntity): Promise<FieldMappingRule[]> => {
    const cached = bullhornMappingCache.get(clientId);
    if (cached && Date.now() - cached.at < BULLHORN_MAPPING_CACHE_MS) {
        return cached.data[entity] || [];
    }
    try {
        const { data } = await supabase
            .from('clients')
            .select('bullhorn_settings')
            .eq('id', clientId)
            .single();
        const settings = (data?.bullhorn_settings || {}) as any;
        const fm = (settings.fieldMappings || {}) as Partial<Record<BullhornMappingEntity, FieldMappingRule[]>>;
        bullhornMappingCache.set(clientId, { at: Date.now(), data: fm });
        return fm[entity] || [];
    } catch {
        return [];
    }
};

const toIntOrUndefined = (value: any): number | undefined => {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
};

// Initialize Supabase client
// Initialize Supabase client
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL || 'https://xquintpfpzdsxupnhypj.supabase.co';
const supabaseKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhxdWludHBmcHpkc3h1cG5oeXBqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY5OTgzNzQsImV4cCI6MjA4MjU3NDM3NH0.QL3q1GsMkgH8tA4p2kFbO2j2kvGATyQmBEj8V-l_ppQ';

// Safe singleton initialization for HMR
let supabaseInstance: any;
if (typeof window !== 'undefined') {
  if (!(window as any).__supabase) {
    (window as any).__supabase = createClient(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });

    // Explicitly update realtime token when auth changes to prevent "sending 'initialize': Unauthorized"
    (window as any).__supabase.auth.onAuthStateChange((event: any, session: any) => {
      if (session?.access_token) {
        (window as any).__supabase.realtime.setAuth(session.access_token);
      }
    });
  }
  supabaseInstance = (window as any).__supabase;
} else {
  // Fallback for SSR or non-browser env
  supabaseInstance = createClient(supabaseUrl, supabaseKey);
}

export const supabase = supabaseInstance;

/** E-mail uit lokale sessie — geen round-trip naar /auth/v1/user (voorkomt 403-ruis bij stale tokens). */
export async function supabase_get_session_user_email(): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return String(session?.user?.email || '').trim();
  } catch {
    return '';
  }
}

/** Uitloggen ook als de server de access token weigert (403). */
export async function supabase_safe_sign_out(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (
    error &&
    ((error as { status?: number }).status === 403 ||
      String(error.message || '').includes('403') ||
      String(error.message || '').toLowerCase().includes('session'))
  ) {
    await supabase.auth.signOut({ scope: 'local' });
  }
}

/** Bulk select for filter UIs (incl. bullhorn_id voor 4-cijferige weergave in filters). */
export const SUPABASE_CLIENT_FILTER_SELECT = 'id, name, logo_url, invoice_kvk, data, bullhorn_id';

/** Prefer explicit client_number column when present, else invoice_kvk / JSON data fields. */
export function pickClientCompanyNumber(row: any): string {
  const d = row?.data;
  const raw =
    row?.client_number ??
    row?.invoice_kvk ??
    (d && typeof d === 'object' ? (d as any).client_number ?? (d as any).company_number : undefined);
  if (raw == null || String(raw).trim() === '') return '';
  return String(raw).trim();
}

const supabaseServiceRoleKey = (import.meta as any).env?.VITE_SUPABASE_SERVICE_ROLE_KEY;

// ADMIN CLIENT (Privileged)
// WARNING: Only use this if you have explicitly decided to expose the Service Key (e.g. internal dashboard)
export const supabaseAdmin = supabaseServiceRoleKey
  ? createClient(supabaseUrl, supabaseServiceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      storageKey: 'supabase-admin-unused' // Prevent conflict with main client
    }
  })
  : null;

/**
 * Opens a file from Supabase Storage in a new browser tab.
 * - Retries up to 3 times on transient errors
 * - Uses a 1-hour signed URL (avoids "expired" issues on slow connections)
 * - Falls back to the public URL if signing keeps failing
 * - Always throws a descriptive Error on hard failure — never silently swallows it
 */
export const openStorageDocument = async (storagePath: string, bucket: string = 'documents'): Promise<void> => {
    const MAX_RETRIES = 3;
    const SIGNED_URL_EXPIRY = 3600; // 1 hour

    let lastError: any;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            const { data, error } = await supabase.storage
                .from(bucket)
                .createSignedUrl(storagePath, SIGNED_URL_EXPIRY);

            if (!error && data?.signedUrl) {
                window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
                return; // Success!
            }
            lastError = error;
            console.warn(`[openStorageDocument] Attempt ${attempt}/${MAX_RETRIES} failed:`, error);
        } catch (e) {
            lastError = e;
            console.warn(`[openStorageDocument] Attempt ${attempt}/${MAX_RETRIES} threw:`, e);
        }

        // Exponential backoff: 100ms, 300ms
        if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 100 * Math.pow(3, attempt - 1)));
        }
    }

    // All signed URL attempts failed — try the public URL as a last resort
    try {
        const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(storagePath);
        if (publicData?.publicUrl) {
            console.warn('[openStorageDocument] Falling back to public URL after signed URL failures.');
            window.open(publicData.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
    } catch (pubErr) {
        console.error('[openStorageDocument] Public URL fallback also failed.', pubErr);
    }

    throw new Error(`Kon document niet openen na ${MAX_RETRIES} pogingen. Controleer je internetverbinding en probeer opnieuw.`);
};

/** Download a file from Supabase Storage (signed URL with Content-Disposition). */
export const downloadStorageDocument = async (
    storagePath: string,
    fileName?: string,
    bucket: string = 'documents',
): Promise<void> => {
    const safeName = (fileName || storagePath.split('/').pop() || 'document').replace(/[\\/:*?"<>|]/g, '').trim() || 'document';
    const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storagePath, 3600, { download: safeName });

    if (error || !data?.signedUrl) {
        throw new Error(error?.message || 'Kon downloadlink niet maken.');
    }

    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.download = safeName;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

/**
 * Invites a user via Supabase Admin API.
 */
export const supabase_admin_invite_user = async (email: string, role: string) => {
  if (!supabaseAdmin) {
    console.error("Supabase Admin client not initialized. Check VITE_SUPABASE_SERVICE_ROLE_KEY.");
    return null;
  }
  const { data, error } = await supabaseAdmin.auth.admin.inviteUserByEmail(email);
  if (error) throw error;
  return data.user;
};

// Also export a helper to list all users (for debugging)
export const supabase_admin_list_users = async () => {
  if (!supabaseAdmin) return [];
  const { data: { users }, error } = await supabaseAdmin.auth.admin.listUsers();
  if (error) throw error;
  console.log("Admin List Users:", users);
  return users;
};

export const supabase_admin_generate_link = async (email: string) => {
  if (!supabaseAdmin) {
    console.error("Supabase Admin client not initialized.");
    return null;
  }
  const { data, error } = await supabaseAdmin.auth.admin.generateLink({
    type: 'magiclink',
    email
  });
  if (error) {
    console.error("Generate link failed:", error);
    throw error;
  }
  return data.properties?.action_link;
};

export const supabase_admin_delete_user = async (uid: string) => {
  if (!supabaseAdmin) throw new Error("Admin client not initialized.");
  
  // Clean up profile first to avoid FK constraints
  await supabaseAdmin.from('profiles').delete().eq('id', uid);
  
  // Delete from auth.users
  const { data, error } = await supabaseAdmin.auth.admin.deleteUser(uid);
  if (error) throw error;
  return data;
};

export const supabase_admin_suspend_user = async (uid: string, suspend: boolean) => {
  if (!supabaseAdmin) {
      const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
      const response = await fetch(`${apiUrl}/${uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ban_duration: suspend ? '876000h' : 'none' })
      });
      if (!response.ok) throw new Error("Failed to suspend user via API fallback");
      return await response.json();
  }
  
  // ban_duration: '876000h' is roughly 100 years. 'none' restores access.
  const { data, error } = await supabaseAdmin.auth.admin.updateUserById(uid, {
    ban_duration: suspend ? '876000h' : 'none'
  });
  if (error) throw error;
  return data;
};

export const supabase_admin_update_user_metadata = async (uid: string, metadata: any) => {
  if (!supabaseAdmin) {
      const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
      const response = await fetch(`${apiUrl}/${uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(metadata)
      });
      if (!response.ok) throw new Error("Failed to update user metadata via API fallback");
      return await response.json();
  }
  
  const { data, error } = await supabaseAdmin.auth.admin.updateUserById(uid, {
    user_metadata: metadata
  });
  if (error) throw error;
  return data;
};

export const supabase_admin_archive_user = async (uid: string) => {
  if (!supabaseAdmin) {
      const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
      const response = await fetch(`${apiUrl}/${uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isArchived: true, ban_duration: '876000h' })
      });
      if (!response.ok) throw new Error("Failed to archive user via API fallback");
      return await response.json();
  }
  
  // 1. Suspend user (Ban for 100 years)
  // 2. Set isArchived in user_metadata
  const { data: user, error: fetchErr } = await supabaseAdmin.auth.admin.getUserById(uid);
  if (fetchErr) throw fetchErr;

  const currentMeta = user?.user?.user_metadata || {};
  
  const { data, error } = await supabaseAdmin.auth.admin.updateUserById(uid, {
    ban_duration: '876000h',
    user_metadata: { ...currentMeta, isArchived: true }
  });
  
  if (error) throw error;
  return data;
};

export const supabase_admin_unarchive_user = async (uid: string) => {
  if (!supabaseAdmin) {
      const apiUrl = window.location.hostname === 'localhost' ? '/api/users' : '/api/users';
      const response = await fetch(`${apiUrl}/${uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isArchived: false, ban_duration: 'none' })
      });
      if (!response.ok) throw new Error("Failed to unarchive user via API fallback");
      return await response.json();
  }
  
  const { data: user, error: fetchErr } = await supabaseAdmin.auth.admin.getUserById(uid);
  if (fetchErr) throw fetchErr;

  const currentMeta = user?.user?.user_metadata || {};
  const { isArchived, ...restMeta } = currentMeta;
  
  const { data, error } = await supabaseAdmin.auth.admin.updateUserById(uid, {
    ban_duration: 'none',
    user_metadata: restMeta
  });
  
  if (error) throw error;
  return data;
};

// Utility
export const extractErrorMessage = (error: any): string => {
  if (!error) return 'Unknown error';

  if (
    error.message === 'Failed to fetch' ||
    error.message === 'Request timed out' ||
    error.message?.includes('Network request failed') ||
    (error.name === 'TypeError' && error.message?.includes('fetch'))
  ) {
    return 'Verbindingsfout: Kan server niet bereiken. Controleer je internetverbinding.';
  }

  if (error.name === 'AbortError' || error.message?.includes('aborted')) {
    return 'Request aborted';
  }

  return error.message || error.error_description || JSON.stringify(error);
};

// --- DATA ACCESS METHODS ---

// Helper to get authorized client IDs (including child clients)
export const supabase_get_authorized_client_ids = async (clientId?: string) => {
    if (!clientId) return [];
    
    // Prevent 400 Bad Request errors on Postgres UUID columns if dirty mock data (like 'c1') is present in the Auth user
    const isUUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(clientId);
    if (!isUUID) {
        console.warn(`[Supabase Service] Invalid UUID format for clientId: "${clientId}". Returning empty permissions to prevent DB 400 Bad Request.`);
        return [];
    }

    try {
        // Alleen eigen organisatie + directe onderliggende werkplekken (parent_client_id = clientId).
        // Niet omhoog klimmen via parent_client_id — dat zou sibling-bedrijven onder dezelfde
        // Werfplaats-tenant blootleggen aan een nieuwe klant.
        const out = new Set<string>([clientId]);
        const { data: children } = await supabase
            .from('clients')
            .select('id')
            .eq('parent_client_id', clientId);
        (children || []).forEach((c: { id: string }) => {
            if (c?.id) out.add(c.id);
        });
        return Array.from(out);
    } catch {
        return [clientId];
    }
};

const chunkUuidList = (ids: string[], size = 8): string[][] => {
    const out: string[][] = [];
    for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
    return out;
};

// Vacancies — list payload (no description blob)
const VACANCY_LIST_SELECT =
  'id, title, status, vacancy_number, created_at, client_id, eindklant_id, opdrachtgever_id, visibility, assigned_recruiter_id, shared_with_users, shared_with_clients, bullhorn_id, data->category, data->publishedToSite, data->lifecycleStatus, data->deadline, data->startDate, data->hoursPerWeek, data->duration, data->clientName, data->location, data->werkplek, data->opdrachtgever_id, data->eindklant_id, data->isClosed, data->closingDate, data->planningDate, data->contactPersonId, data->tags, data->booleanString, data->bullhorn_contact, data->quick_note, data->quick_note_author, clients!vacancies_client_id_fkey(id, name, logo_url), eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url), recruiter:profiles!vacancies_assigned_recruiter_id_fkey(id, name, email, phone)';

const formatVacancyListRows = (data: any[]) =>
  data.map((row: any) => {
    const reconstructedData = {
      category: row.category,
      publishedToSite: row.publishedToSite,
      lifecycleStatus: row.lifecycleStatus,
      deadline: row.deadline,
      startDate: row.startDate,
      hoursPerWeek: row.hoursPerWeek,
      duration: row.duration,
      clientName: row.clientName,
      location: row.location,
      werkplek: row.werkplek,
      opdrachtgever_id: row.opdrachtgever_id,
      eindklant_id: row.eindklant_id,
      isClosed: row.isClosed,
      closingDate: row.closingDate,
      planningDate: row.planningDate,
      contactPersonId: row.contactPersonId,
      tags: row.tags,
      booleanString: row.booleanString,
      bullhorn_contact: row.bullhorn_contact,
      quick_note: row.quick_note,
      quick_note_author: row.quick_note_author,
    };
    return {
      ...row,
      data: reconstructedData,
      _isLight: true,
    };
  });

/**
 * @param portalLinksOnly — clientportaal: vacatures gekoppeld aan de ingelogde organisatie (CRM-id uit o.a.
 * `client_contacts.linked_client_id`): `opdrachtgever_id` of `eindklant_id` gelijk aan die UUID. Vereist
 * geldige `clientId` (anders lege lijst). Gearchiveerde vacatures worden client-side uitgesloten.
 */
export const supabase_get_vacancies = async (
  clientId?: string,
  onProgress?: (progress: number) => void,
  portalLinksOnly?: boolean
) => {
  const portalLog = (msg: string, ...extra: unknown[]) => {
    if (portalLinksOnly) console.info(`[ClientportalVacatures] ${msg}`, ...extra);
  };
  const portalWarn = (msg: string, ...extra: unknown[]) => {
    if (portalLinksOnly) console.warn(`[ClientportalVacatures] ${msg}`, ...extra);
  };
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

  try {
    if (portalLinksOnly && (!clientId || !String(clientId).trim())) {
      portalWarn('Afgebroken: geen clientId (filter) — vacatures worden niet opgevraagd.', {
        clientId: clientId ?? null,
        raw: clientId,
      });
      return [];
    }

    if (portalLinksOnly) {
      console.groupCollapsed(
        `[ClientportalVacatures] Supabase fetch — org ${String(clientId || '').slice(0, 8)}…`
      );
      portalLog('Parameters', {
        clientId: String(clientId || '').trim(),
        portalLinksOnly: true,
        timestampIso: new Date().toISOString(),
      });
    }

    let allowedClientIds: string[] | null = null;
    if (clientId) {
      if (portalLinksOnly) {
        allowedClientIds = [String(clientId).trim()];
      } else {
        allowedClientIds = await supabase_get_authorized_client_ids(clientId);
      }
    }

    if (allowedClientIds && allowedClientIds.length === 0) {
        console.warn("[supabase_get_vacancies] Empty allowedClientIds resolved from dirty mock ID! Aborting query to prevent .in() syntax error.");
        if (portalLinksOnly) console.groupEnd();
        return [];
    }

    if (portalLinksOnly) {
      portalLog('Toegestane org-UUIDs (chunks)', {
        count: allowedClientIds?.length ?? 0,
        ids: allowedClientIds,
      });
    }

    const CHUNK_SIZE = 8;
    const clientChunks: (string[] | null)[] = [];
    if (allowedClientIds && allowedClientIds.length > CHUNK_SIZE) {
        for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) clientChunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
    } else {
        clientChunks.push(allowedClientIds);
    }

    const isMultiClientScope = Boolean(allowedClientIds && allowedClientIds.length > 1);
    let allData: any[] = [];
    const PAGE = portalLinksOnly ? 500 : isMultiClientScope ? 400 : 1000;
    const MAX_ROWS = portalLinksOnly ? 3000 : isMultiClientScope ? 2500 : 10000;
    const PARALLEL = isMultiClientScope ? 1 : 2;

    const buildBaseQuery = (chunkIds: string[] | null) => {
      let query = supabase.from('vacancies').select(VACANCY_LIST_SELECT).order('created_at', { ascending: false });
      if (chunkIds && chunkIds.length > 0) {
        const clean = chunkIds.map((x) => String(x || '').trim()).filter(Boolean);
        // Portaal: één org-id — PostgREST `.in.(uuid)` is soms gevoelig; `.eq` per kolom is betrouwbaar.
        if (portalLinksOnly && clean.length === 1) {
          const id = clean[0];
          // PostgREST: UUID met streepjes moet tussen dubbele aanhalingstekens in .or()-expressies.
          const q = `"${id}"`;
          const orExpr = `opdrachtgever_id.eq.${q},eindklant_id.eq.${q}`;
          if (portalLinksOnly) portalLog('PostgREST .or() opdrachtgever/eindklant (portaal)', { orExpr, uuid: id });
          query = query.or(orExpr);
        } else {
          const idsString = `(${clean.join(',')})`;
          const orExpr = `client_id.in.${idsString},opdrachtgever_id.in.${idsString},eindklant_id.in.${idsString}`;
          if (portalLinksOnly) portalLog('PostgREST .or() (niet-portaal chunk)', { orExpr, chunkIds: clean });
          query = query.or(orExpr);
        }
      }
      return query;
    };

    let chunkIndex = 0;
    for (const chunkIds of clientChunks) {
        chunkIndex += 1;
        let hasMore = true;
        let from = 0;
        let pageRound = 0;

        const isRangeNotSatisfiable = (err: any) => {
            const msg = String(err?.message || '').toLowerCase();
            return (
                err?.code === 'PGRST103' ||
                msg.includes('range not satisfiable') ||
                msg.includes('requested range')
            );
        };

        while (hasMore && from < MAX_ROWS) {
            pageRound += 1;
            const range1End = from + PAGE - 1;
            const range2Start = from + PAGE;
            const range2End = from + PARALLEL * PAGE - 1;
            const runSecondBatch = PARALLEL > 1 && range2End >= range2Start;

            const q1 = buildBaseQuery(chunkIds).range(from, range1End);
            const fetchPromises: ReturnType<typeof q1>[] = [q1];
            if (runSecondBatch) {
                fetchPromises.push(buildBaseQuery(chunkIds).range(range2Start, range2End));
            }

            const results = await Promise.all(fetchPromises);
            const { data: d1, error: e1 } = results[0] || {};
            const d2 = runSecondBatch ? results[1]?.data : [];
            const e2 = runSecondBatch ? results[1]?.error : null;

            if (e1) {
              if (isRangeNotSatisfiable(e1)) {
                hasMore = false;
                break;
              }
              if (portalLinksOnly) {
                portalWarn('Query-range fout (batch 1)', {
                  chunkIndex,
                  pageRound,
                  range: [from, range1End],
                  code: (e1 as any)?.code,
                  message: (e1 as any)?.message,
                  details: (e1 as any)?.details,
                  hint: (e1 as any)?.hint,
                  full: e1,
                });
              }
              throw e1;
            }
            if (e2) {
              if (isRangeNotSatisfiable(e2)) {
                /* eerste batch ok — geen verdere pagina's */
              } else {
                if (portalLinksOnly) {
                  portalWarn('Query-range fout (batch 2)', {
                    chunkIndex,
                    pageRound,
                    range: [range2Start, range2End],
                    code: (e2 as any)?.code,
                    message: (e2 as any)?.message,
                    details: (e2 as any)?.details,
                    hint: (e2 as any)?.hint,
                    full: e2,
                  });
                }
                throw e2;
              }
            }

            const batch = [...(d1 || []), ...(d2 || [])];
            if (portalLinksOnly) {
              portalLog(`Chunk ${chunkIndex} ronde ${pageRound}`, {
                from,
                lenBatch1: d1?.length ?? 0,
                lenBatch2: d2?.length ?? 0,
                batchTotal: batch.length,
                cumulatiefInMemory: allData.length + batch.length,
              });
            }
            if (batch.length === 0) {
                hasMore = false;
                if (portalLinksOnly && pageRound === 1 && from === 0) {
                  portalLog('Eerste batch leeg — geen rijen voor deze filter (RLS of geen match).');
                }
                break;
            }

            allData = allData.concat(formatVacancyListRows(batch));

            const len1 = d1?.length || 0;
            const len2 = (d2 as any[])?.length || 0;
            if (len1 < PAGE) hasMore = false;
            else if (runSecondBatch && len2 < PAGE) hasMore = false;
            else hasMore = from + (runSecondBatch ? PARALLEL * PAGE : PAGE) < MAX_ROWS;

            from += runSecondBatch ? PARALLEL * PAGE : PAGE;
            if (onProgress) {
                onProgress(Math.min(99, Math.round((from / MAX_ROWS) * 100)));
            }
        }
    }
    if (onProgress) onProgress(100);

    if (portalLinksOnly) {
      const ms = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
      const sample = allData.slice(0, 8).map((v: any) => ({
        id: v.id,
        title: v.title,
        status: v.status,
        lifecycleStatus: v?.data?.lifecycleStatus ?? null,
        client_id: v.client_id ?? null,
        eindklant_id: v.eindklant_id ?? null,
        opdrachtgever_id: v.opdrachtgever_id ?? null,
      }));
      portalLog('Resultaat', {
        totaalRijen: allData.length,
        duurMs: Math.round(ms),
      });
      if (sample.length) portalLog('Steekproef (max 8)', sample);
      else portalLog('Geen vacatures — controleer RLS, eindklant_id/opdrachtgever_id/client_id en client_contacts.');
      console.groupEnd();
    }

    if (portalLinksOnly) {
      allData = filterPortalVisibleVacancies(allData);
    }

    return allData;
  } catch (error: any) {
    if (error.name === 'AbortError' || error.message?.includes('aborted')) return [];
    console.warn("supabase_get_vacancies failed:", error?.message || error, portalLinksOnly ? { portalLinksOnly, clientId } : { clientId });
    if (portalLinksOnly) {
      console.warn('[ClientportalVacatures] Fout — details', {
        name: error?.name,
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        clientId: clientId ?? null,
      });
      try {
        console.groupEnd();
      } catch {
        /* group niet open */
      }
    }
    if (onProgress) onProgress(100);
    return [];
  }
};

export const supabase_get_vacancy_by_id = async (vacancyId: string) => {
    try {
        const { data, error } = await supabase.from('vacancies')
           .select('*, clients!vacancies_client_id_fkey(*), eindklant:clients!vacancies_eindklant_id_fkey(*), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(*), recruiter:profiles!vacancies_assigned_recruiter_id_fkey(id, name, email, phone)')
           .eq('id', vacancyId)
           .single();
        if (error) throw error;
        return data;
    } catch (e) {
        console.error("fetch single vacancy error:", e);
        return null;
    }
};

export const supabase_get_vacancy_activity_timeline = async (vacancyId: string) => {
  try {
    const formatRel = (ts: number) => {
        const d = new Date(ts);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const timeStr = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        
        if (diffDays === 0 && now.getDate() === d.getDate()) return `Vandaag, ${timeStr}`;
        const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
        if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) return `Gisteren, ${timeStr}`;
        if (diffDays < 7) return `${diffDays} dagen geleden`;
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const logs: any[] = [];
    const { data: vac, error: vacErr } = await supabase.from('vacancies').select('created_at, data').eq('id', vacancyId).single();
    
    if (vac && !vacErr) {
      logs.push({
        id: `vac-${vacancyId}`,
        type: 'create',
        action: 'Vacature aangemaakt',
        user: 'Recruiter',
        timestamp: new Date(vac.created_at).getTime()
      });
    }

    const flowHist = Array.isArray((vac as any)?.data?.flowStepHistory)
      ? ([...(vac as any).data.flowStepHistory] as any[])
      : [];
    flowHist.forEach((entry: any, idx: number) => {
      if (!entry?.at) return;
      if (entry.kind === 'navigate') return;

      const ts = new Date(entry.at).getTime();
      const summaries = Array.isArray(entry.changeSummaries) ? entry.changeSummaries.filter(Boolean) : [];
      const who = entry.userName || entry.userEmail || 'Onbekende gebruiker';
      const isIntake = entry.kind === 'intake_save';
      const isSave = entry.kind === 'save';
      let action = 'Vacature bijgewerkt';
      if (isIntake) {
        action = summaries.length
          ? `Intake: wijzigingen opgeslagen`
          : 'Intake opgeslagen';
      } else if (isSave) {
        action = summaries.length
          ? `${entry.stepLabel || 'Stap'}: wijzigingen opgeslagen`
          : `${entry.stepLabel || 'Stap'}: opgeslagen`;
      }
      logs.push({
        id: `flow-${vacancyId}-${idx}-${entry.at}`,
        type: 'flow_step',
        action,
        user: who,
        userRole: entry.userRole,
        userEmail: entry.userEmail,
        stepLabel: entry.stepLabel,
        kind: entry.kind,
        changeSummaries: summaries,
        timestamp: ts,
      });
    });

    const { data: apps, error: appErr } = await supabase
      .from('applications')
      .select('id, created_at, status, match_score, candidates(name)')
      .eq('vacancy_id', vacancyId);

    if (apps && !appErr) {
      apps.forEach(app => {
         const candName = app.candidates?.name || 'Onbekende kandidaat';
         const ts = new Date(app.created_at).getTime();
         logs.push({
            id: `app-${app.id}`,
            type: 'status',
            action: `Nieuwe sollicitatie van ${candName}`,
            user: 'Kandidaat',
            timestamp: ts
         });

         if (app.match_score > 0) {
             logs.push({
                id: `ai-${app.id}`,
                type: 'match',
                action: `AI Match screening (${app.match_score}%) voor ${candName}`,
                user: 'Froukje AI',
                timestamp: ts + 2000
             });
         }
         
         if (app.status && app.status !== 'New') {
             logs.push({
                id: `stat-${app.id}`,
                type: 'edit',
                action: `Status gewijzigd naar '${app.status}' voor ${candName}`,
                user: 'Recruiter',
                timestamp: ts + 5000
             });
         }
      });
    }

    logs.sort((a, b) => b.timestamp - a.timestamp);
    return logs.map((log: any) => ({
       ...log,
       date: formatRel(log.timestamp),
       absoluteDate: new Date(log.timestamp).toLocaleString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    }));
  } catch (error) {
    console.error("Error fetching activity timeline:", error);
    return [];
  }
};

// Reviews
export const supabase_get_vacancy_stats = async (vacancyId: string) => {
  try {
    const { count: total, error: errTotal } = await supabase
      .from('applications')
      .select('*', { count: 'estimated', head: true })
      .eq('vacancy_id', vacancyId);

    const { count: screening, error: errScreening } = await supabase
      .from('applications')
      .select('*', { count: 'estimated', head: true })
      .eq('vacancy_id', vacancyId)
      .eq('status', 'Screening');

    const { count: interview, error: errInterview } = await supabase
      .from('applications')
      .select('*', { count: 'estimated', head: true })
      .eq('vacancy_id', vacancyId)
      .eq('status', 'Interview');

    const { count: offer, error: errOffer } = await supabase
      .from('applications')
      .select('*', { count: 'estimated', head: true })
      .eq('vacancy_id', vacancyId)
      .eq('status', 'Offer');

    // 'New' is applications with status 'New' OR explicitly null/undefined if that's how it's stored
    // Based on KanbanBoard, new is status 'New' or !status
    const { count: statNew, error: errNew } = await supabase
      .from('applications')
      .select('*', { count: 'estimated', head: true })
      .eq('vacancy_id', vacancyId)
      .or('status.eq.New,status.is.null');

    if (errTotal) throw errTotal;

    return {
      total: total || 0,
      new: statNew || 0,
      screening: screening || 0,
      interview: interview || 0,
      offer: offer || 0
    };
  } catch (error) {
    console.error("Error fetching vacancy stats:", error);
    return { total: 0, new: 0, screening: 0, interview: 0, offer: 0 };
  }
};

// Reviews
export const supabase_get_candidate_reviews = async (vacancyId: string, candidateId: string) => {
  try {
    const { data, error } = await supabase
      .from('candidate_reviews')
      .select('*')
      .eq('vacancy_id', vacancyId)
      .eq('candidate_id', candidateId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching reviews:", error);
    return [];
  }
};

export const supabase_save_review = async (review: { candidate_id: string, vacancy_id: string, reviewer_id: string, score: number, feedback: string, reviewer_name?: string }) => {
  try {
    const { data, error } = await supabase
      .from('candidate_reviews')
      .insert([review])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error saving review:", error);
    return null;
  }
};

// ... (omitting insert/update/delete/get_clients/get_client_branding as they are unchanged) ...

// Talent Pool & Candidates
export const supabase_insert_vacancy = async (vacancy: any) => {
  if (vacancy.vacancy_number === undefined) {
    try {
      // Fetch last few vacancy numbers from non-bullhorn vacancies to find the correct next number
      const { data: maxRows } = await supabase
        .from('vacancies')
        .select('vacancy_number, bullhorn_id')
        .order('vacancy_number', { ascending: false })
        .limit(50);

      let maxNum = 1000;
      if (maxRows && maxRows.length > 0) {
        // Prefer the highest number from vacancies that are NOT Bullhorn imports
        const manualNums = maxRows
          .filter(r => !r.bullhorn_id && typeof r.vacancy_number === 'number')
          .map(r => r.vacancy_number as number);

        if (manualNums.length > 0) {
          maxNum = Math.max(...manualNums);
        } else {
          // All are bullhorn imports — use highest overall but cap fallback
          const allNums = maxRows
            .filter(r => typeof r.vacancy_number === 'number')
            .map(r => r.vacancy_number as number);
          maxNum = allNums.length > 0 ? Math.max(...allNums) : 1000;
        }
      }
      vacancy.vacancy_number = maxNum + 1;
    } catch (e) {
      console.warn("Could not determine max vacancy_number, falling back", e);
      vacancy.vacancy_number = Math.floor(Date.now() / 1000); // timestamp fallback
    }
  }

  const payload = { ...vacancy };
  if ('clientId' in payload) {
      payload.client_id = payload.clientId;
      delete payload.clientId;
  }
  
  // Default client_id for Werfplaats as requested
  if (!payload.client_id) {
    if (localStorage.getItem('werftools_user_client_id')) {
        payload.client_id = localStorage.getItem('werftools_user_client_id');
    } else {
        payload.client_id = '62288053-c24c-4bfe-8ae9-b6b239b12469';
    }
  }

  const { data, error } = await supabase.from('vacancies').insert([payload]).select().single();
  if (error) throw error;
  return data;
};

export const supabase_update = async (id: string, updates: any) => {
  try {
    const finalUpdates = { ...updates };
    const isClientPortal =
      typeof window !== 'undefined' &&
      (window.location.hostname.toLowerCase().includes('clientportal') ||
        new URLSearchParams(window.location.search).get('clientportal') === 'true');
    if (isClientPortal && 'status' in finalUpdates) {
      delete finalUpdates.status;
    }
    if ('clientId' in finalUpdates) {
      finalUpdates.client_id = finalUpdates.clientId;
      delete finalUpdates.clientId;
    }
    
    // Prevent "invalid input syntax for type uuid: ''" errors on foreign keys
    const uuidFields = ['client_id', 'eindklant_id', 'contactPersonId', 'assignedRecruiterId', 'assigned_recruiter_id'];
    uuidFields.forEach(f => {
       if (finalUpdates[f] === "" || finalUpdates[f] === "undefined") {
           finalUpdates[f] = null;
       }
    });

    const { error } = await supabase.from('vacancies').update(finalUpdates).eq('id', id);
    if (error) {
      console.warn(`supabase_update failed for ${id}:`, error.message);
      // Don't throw, just let it slide to prevent UI crash loops
    }
  } catch (e) {
    console.warn("supabase_update exception:", e);
  }
};

export const supabase_delete_vacancy = async (id: string) => {
  const { error } = await supabase.from('vacancies').delete().eq('id', id);
  if (error) throw error;
};

// Tenders
export const supabase_get_tenders = async (clientId?: string) => {
  try {
    let query = supabase.from('tenders').select('*').order('created_at', { ascending: false });
    if (clientId) { const allIds = await supabase_get_authorized_client_ids(clientId); query = query.in('client_id', allIds); }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error: any) {
    if (error.name === 'AbortError' || error.message?.includes('aborted')) return [];
    console.warn("supabase_get_tenders failed:", error);
    return [];
  }
};

export const supabase_save_tender = async (tender: any) => {
  const { data, error } = await supabase.from('tenders').upsert(tender).select().single();
  if (error) throw error;
  return data;
};

export const supabase_delete_tender = async (id: string) => {
  const { error } = await supabase.from('tenders').delete().eq('id', id);
  if (error) throw error;
};

// Clients
export const supabase_get_clients = async (clientId?: string) => {
  try {
    let query = supabase.from('clients').select('*');
    if (clientId) {
      const allowedIds = await supabase_get_authorized_client_ids(clientId);
      if (allowedIds.length === 0) return [];
      if (allowedIds.length === 1) query = query.eq('id', allowedIds[0]);
      else query = query.in('id', allowedIds);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

export const supabase_get_client_branding = async (clientId?: string) => {
  if (!clientId || clientId === 'undefined') return null;
  try {
    const { data, error } = await supabase.from('client_branding')
      .select('config')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(1);
    // If table doesn't exist (404) or other error, just return null, don't crash/log
    if (error) return null;
    return data?.[0]?.config || null;
  } catch (error) {
    return null;
  }
};

export const supabase_update_client_branding = async (clientId: string, config: any) => {
  const adminClient = supabaseAdmin || supabase;
  
  // Bridge customDomain upward to ensure the Tenant router can query it natively
  if (config && typeof config.customDomain === 'string') {
    await adminClient.from('clients')
      .update({ custom_domain: config.customDomain || null })
      .eq('id', clientId);
  }

  // Handle case where multiple rows exist due to missing unique constraint
  const { data: existingRows } = await adminClient.from('client_branding').select('id').eq('client_id', clientId).limit(1);

  let err;
  if (existingRows && existingRows.length > 0) {
    // Update all matching rows to ensure consistency
    const { error } = await adminClient.from('client_branding').update({ config }).eq('client_id', clientId);
    err = error;
  } else {
    // We insert a new row
    const { error } = await adminClient.from('client_branding').insert([{ client_id: clientId, config }]);
    err = error;
  }

  if (err) {
    console.warn("branding update failed:", err.message);
    throw err;
  }
};

export const supabase_upload_client_logo = async (clientId: string, file: File) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `client-logos/${clientId}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const { error } = await supabase.storage.from('public').upload(fileName, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('public').getPublicUrl(fileName);
  return data.publicUrl;
};

export const supabase_update_client = async (id: string, updates: any) => {
  const { error } = await supabase.from('clients').update(updates).eq('id', id);
  if (error) throw error;
};

// Recruiters & Team Members
export const supabase_get_recruiters = async (clientId?: string) => {
  let query = supabase.from('profiles').select('*').in('role', ['RECRUITER', 'ADMIN', 'CLIENT', 'CLIENT_RECRUITER']);
  if (clientId) { const allIds = await supabase_get_authorized_client_ids(clientId); query = query.in('client_id', allIds); }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/** Volgende vrije `clients.bullhorn_id` = max bestaand numeriek id + 1. */
export const supabase_get_next_client_bullhorn_id = async (): Promise<string> => {
  const pageSize = 1000;
  let offset = 0;
  let max = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('clients')
      .select('bullhorn_id')
      .not('bullhorn_id', 'is', null)
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    if (!data?.length) break;
    for (const row of data) {
      const n = parseInt(String((row as { bullhorn_id?: unknown }).bullhorn_id ?? '').replace(/\D/g, ''), 10);
      if (Number.isFinite(n) && n > max) max = n;
    }
    if (data.length < pageSize) break;
    offset += pageSize;
  }
  return String(max + 1);
};

export const supabase_insert_client = async (client: any) => {
  const row = { ...client };
  const existing = row.bullhorn_id != null ? String(row.bullhorn_id).trim() : '';
  if (!existing) {
    try {
      row.bullhorn_id = await supabase_get_next_client_bullhorn_id();
    } catch (e) {
      console.warn('[supabase_insert_client] next bullhorn_id failed', e);
    }
  }
  const { data, error } = await supabase.from('clients').insert([row]).select().single();
  if (error) throw error;
  return data;
};

export const supabase_delete_client = async (id: string) => {
  const { error } = await supabase.from('clients').delete().eq('id', id);
  if (error) throw error;
};

// Site Config (CMS)
export const supabase_get_site_config = async () => {
  try {
    const { data, error } = await supabase.from('site_config').select('config').maybeSingle();
    if (error) {
      if (error.message?.includes('Failed to fetch') || error.message?.includes('Network')) return null;
      if (error.code !== 'PGRST116') console.warn("get_site_config warning:", error.message);
    }
    return data?.config || null;
  } catch (e) {
    return null;
  }
};

export const supabase_save_site_config = async (config: any) => {
  const { error } = await supabase.from('site_config').upsert({ id: 1, config });
  if (error) throw error;
};

export const supabase_upload_website_asset = async (file: File) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `website/${Math.random()}.${fileExt}`;
  const { error } = await supabase.storage.from('public').upload(fileName, file);
  if (error) throw error;
  const { data } = supabase.storage.from('public').getPublicUrl(fileName);
  return data.publicUrl;
};

export const check_domain_verification = async (domain: string) => {
  return true;
};

// List views: omit original_cv_text (large) to avoid statement timeouts on scoped .in(client_id) queries.
const CANDIDATE_LIST_COLS_LIGHT =
  "id, client_id, name, email, role, experience, status, tags, avatar_url, added_at, phone, linkedin_url, salary_range, availability, updated_at, photo_url, address_city, address_zip, address_country, source, employment_preference, skill_set, candidate_number, ai_parsed_data, cv_summary";

/** Full row incl. CV body — use only for detail/export, not paginated list. */
const CANDIDATE_LIST_COLS =
  `${CANDIDATE_LIST_COLS_LIGHT}, original_cv_text`;

const mapTalentPoolCandidateRow = (c: any) => ({
    id: c.id,
    client_id: c.client_id,
    candidate_id: c.id,
    status: c.status,
    ai_parsed_data: c.ai_parsed_data,
    parsed_cv: c.ai_parsed_data,
    last_contact_at: c.updated_at,
    candidate: { ...c },
});

const CLIENT_ID_FILTER_CHUNK = 8;
/** Max rows merged in memory for multi-client talent pool pagination (sort keys only). */
const TALENT_POOL_MERGE_CAP = 30000;
const TALENT_POOL_SORT_KEY_COLS = 'id, name, candidate_number, added_at, status';
const TALENT_POOL_ID_FETCH_STEP = 1000;

// Talent Pool & Candidates
export const supabase_get_talent_pool = async (clientId?: string, onMoreLoaded?: (allData: any[]) => void, onProgress?: (pct: number) => void) => {
  try {
    let allData: any[] = [];
    let totalCount = 0;
    
    let allowedClientIds: string[] | null = null;
    if (clientId) {
      allowedClientIds = await supabase_get_authorized_client_ids(clientId);
      if (allowedClientIds.length === 0) {
          console.warn("[supabase_get_talent_pool] Empty allowedClientIds resolved from dirty mock ID! Aborting query to prevent .in() syntax error.");
          return [];
      }
    }
    
    // 1. Fetch exact total count first
    const countQuery = supabase.from('candidates').select('*', { count: 'estimated', head: true });
    if (allowedClientIds) {
        if (allowedClientIds.length > 20) {
           // Simplification for large client lists: roughly estimate or just fallback to 1000
           totalCount = 1000; 
        } else {
           countQuery.in('client_id', allowedClientIds);
           const { count } = await countQuery;
           totalCount = count || 1000;
        }
    } else {
        const { count } = await countQuery;
        totalCount = count || 1000;
    }
    
    if (onProgress) onProgress(5); // initial progress
    let from = 0;
    const step = 500; // fetch in chunks of 500 to prevent 8-second timeout
    let hasMore = true;
    let isFirstChunk = true;
    while (hasMore) {
      const CHUNK_SIZE = 20;
      const clientChunks: (string[] | null)[] = [];
      if (allowedClientIds && allowedClientIds.length > CHUNK_SIZE) {
          for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) clientChunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
      } else {
          clientChunks.push(allowedClientIds);
      }

        const promises = clientChunks.map(async (chunkIds) => {
          let query = supabase
            .from('candidates')
            .select(`*`)
            .order('added_at', { ascending: false })
            .range(from, from + step - 1);
          if (chunkIds) query = query.in('client_id', chunkIds);
          return query;
      });

      const resArray = await Promise.all(promises);
      const data = resArray.flatMap(r => r.data || []);
      const anyError = resArray.find(r => r.error);
      if (anyError) throw anyError.error;
      if (data && data.length > 0) {
          const seenIdsFirst = new Set(allData.map(item => item.id));
          const newUniqueDataFirst = data.filter((item: any) => !seenIdsFirst.has(item.id));
          allData = [...allData, ...newUniqueDataFirst];
          
          if (onProgress) {
             const pct = Math.min(99, Math.round((allData.length / totalCount) * 100));
             onProgress(pct);
          }
          
          if (isFirstChunk && data.length >= step && onMoreLoaded) {
             isFirstChunk = false;
             from += step;
             (async () => {
                 let bgHasMore = true;
                 let loopCount = 0; // Guard against infinite background loops freezing the browser
                 while (bgHasMore && loopCount < 5) { // Max 2500 candidates fetched in background to save memory
                     loopCount++;
                     const bgPromises = clientChunks.map(async (chunkIds) => {
                         let bgQuery = supabase
                             .from('candidates')
                             .select(`*`)
                             .order('added_at', { ascending: false })
                             .range(from, from + step - 1);
                         if (chunkIds) bgQuery = bgQuery.in('client_id', chunkIds);
                         return bgQuery;
                     });
                     
                     try {
                         const bgResArray = await Promise.all(bgPromises);
                         const bgData = bgResArray.flatMap(r => r.data || []);
                         
                         if (bgData && bgData.length > 0) {
                             // Faster deduplication
                             const seenIds = new Set(allData.map(item => item.id));
                             const newUniqueData = bgData.filter(item => !seenIds.has(item.id));
                             allData = [...allData, ...newUniqueData];

                             // Only call onMoreLoaded if we actually added new data
                             if (newUniqueData.length > 0) {
                                onMoreLoaded([...allData]);
                             }
                             
                             if (onProgress) {
                               const bgPct = Math.min(100, Math.round((allData.length / totalCount) * 100));
                               onProgress(bgPct);
                             }
                             
                             const hitLimit = bgResArray.some(r => r.data && r.data.length >= step);
                             if (!hitLimit) {
                                 bgHasMore = false;
                                 if (onProgress) onProgress(100);
                             } else {
                                 from += step;
                             }
                         } else {
                             bgHasMore = false;
                             if (onProgress) onProgress(100);
                         }
                     } catch (err) {
                         console.warn("Background fetch error:", err);
                         bgHasMore = false;
                         if (onProgress) onProgress(100);
                     }
                     // Add a small delay to prevent blocking the main thread
                     await new Promise(res => setTimeout(res, 300));
                 }
                 if (bgHasMore && onProgress) onProgress(100); // Complete progress if we hit the limit
             })();
             hasMore = false;
          } else {
             if (data.length < step) {
                 hasMore = false;
             } else {
                 from += step;
             }
          }
      } else {
          hasMore = false;
      }
    }
    
    const uniqueMap = new Map();
    for (const d of allData) {
        if (!uniqueMap.has(d.candidate_id)) {
            uniqueMap.set(d.candidate_id, d);
        }
    }
    
    if (onProgress) onProgress(100);
    
    return Array.from(uniqueMap.values());
  } catch (error: any) {
    if (onProgress) onProgress(100);
    if (error.name === 'AbortError' || error.message?.includes('aborted')) return [];
    console.warn("Talent pool fetch error:", error);
    return [];
  }
};

export const supabase_get_candidates_light = async (clientId?: string) => {
  try {
    let allowedClientIds: string[] | null = null;
    if (clientId) {
      allowedClientIds = await supabase_get_authorized_client_ids(clientId);
    }

    let allowedCandidateIds: string[] = [];
    // Synchronous loop removed for performance reasons. Client visibility is handled via client_id.

    if (allowedClientIds && allowedClientIds.length === 0) {
        console.warn("[supabase_get_talent_pool] Empty allowedClientIds! Aborting query to prevent .in() syntax error.");
        return [];
    }
    const CHUNK_SIZE = 10;
    const clientChunks: (string[] | null)[] = [];
    if (allowedClientIds && allowedClientIds.length > CHUNK_SIZE) {
        for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) clientChunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
    } else {
        clientChunks.push(allowedClientIds);
    }

    let allData: any[] = [];
    const step = 999; 

    for (const chunkIds of clientChunks) {
        let hasMore = true;
        let from = 0;
        
        while (hasMore) {
            let query = supabase
              .from('candidates')
              .select(`${CANDIDATE_LIST_COLS_LIGHT}`)
              .order('added_at', { ascending: false })
              .range(from, from + step - 1);

            if (chunkIds && chunkIds.length > 0) {
                const cIdsString = `(${chunkIds.join(',')})`;
                if (allowedCandidateIds.length > 0) {
                     const candIdsStr = `(${allowedCandidateIds.slice(0, 20).join(',')})`;
                     query = query.or(`client_id.in.${cIdsString},id.in.${candIdsStr}`);
                } else {
                     query = query.in('client_id', chunkIds);
                }
            }
            
            const { data, error } = await query;
            if (error) throw error;
            
            if (data && data.length > 0) {
                const mappedData = data.map((c: any) => ({
                    id: c.id, 
                    client_id: c.client_id,
                    candidate_id: c.id,
                    status: c.status,
                    ai_parsed_data: c.ai_parsed_data,
                    parsed_cv: c.ai_parsed_data, 
                    last_contact_at: c.updated_at,
                    candidate: { ...c }
                }));
                allData.push(...mappedData);
                if (data.length < step) hasMore = false;
                else from += step;
            } else {
                hasMore = false;
            }
        }
    }

    return allData;
  } catch (error) {
    console.warn("Light candidates fetch error:", error);
    return [];
  }
};

export const supabase_get_paginated_talent_pool = async (
    clientId?: string,
    page: number = 1,
    limit: number = 20, 
    searchQuery: string = '', 
    orderBy: string = 'created_at', 
    ascending: boolean = false,
    excludeShortlist: boolean = false,
    filters?: any
) => {
  try {
    const from = (page - 1) * limit;
    
    let allowedClientIds: string[] | null = null;
    if (clientId) {
      allowedClientIds = await supabase_get_authorized_client_ids(clientId);
    }
    
    const allowedCandidateIds: string[] = [];
    const aiIdsRestricting =
        Array.isArray(filters?.aiFilterIds) && filters.aiFilterIds.length > 0;
    const hasScopedCandidateIds =
        filters?.scopedCandidateIds !== undefined && filters.scopedCandidateIds !== null;

    const applyTalentPoolFilters = (query: any) => {
        let q = query;

        if (excludeShortlist) {
            q = q.not('status', 'ilike', '%shortlist%');
        }

        if (filters?.statusVariants && Array.isArray(filters.statusVariants) && filters.statusVariants.length > 0) {
            const parts: string[] = [];
            const seen = new Set<string>();
            for (const rawVar of filters.statusVariants) {
                const v = String(rawVar || '').trim();
                if (!v) continue;
                const key = v.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                const esc = v.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
                parts.push(`status.ilike.${esc}`);
                parts.push(`status.ilike.${esc} %`);
                parts.push(`status.ilike.${esc}-%`);
            }
            if (parts.length > 0) q = q.or(parts.join(','));
        } else if (filters?.status) {
            const v = String(filters.status).trim();
            if (v) {
                const esc = v.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
                q = q.or(`status.ilike.${esc},status.ilike.${esc} %,status.ilike.${esc}-%`);
            }
        }

        if (filters?.aiFilterIds !== undefined && filters.aiFilterIds !== null) {
            const ids = Array.isArray(filters.aiFilterIds) ? filters.aiFilterIds : [];
            q = ids.length > 0
                ? q.in('id', ids)
                : q.in('id', ['00000000-0000-0000-0000-000000000000']);
        }

        if (hasScopedCandidateIds) {
            const ids = Array.isArray(filters.scopedCandidateIds) ? filters.scopedCandidateIds : [];
            q = ids.length === 0
                ? q.in('id', ['00000000-0000-0000-0000-000000000000'])
                : q.in('id', ids.slice(0, 1000));
        }

        const dr = filters?.dateRange as string | undefined;
        if (dr && dr !== 'all') {
            const now = new Date();
            if (dr === '7days') {
                q = q.gte('added_at', new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString());
            } else if (dr === '30days') {
                q = q.gte('added_at', new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString());
            } else if (dr === 'thisYear') {
                const y = now.getFullYear();
                q = q.gte('added_at', `${y}-01-01T00:00:00`).lte('added_at', `${y}-12-31T23:59:59.999`);
            } else if (dr === 'custom' && filters.dateFrom && filters.dateTo) {
                let df = String(filters.dateFrom).trim();
                let dt = String(filters.dateTo).trim();
                if (df > dt) {
                    const swap = df;
                    df = dt;
                    dt = swap;
                }
                q = q.gte('added_at', `${df}T00:00:00`).lte('added_at', `${dt}T23:59:59.999`);
            }
        }

        if (searchQuery && !aiIdsRestricting) {
            const trimmed = searchQuery.trim();
            const sq = searchDbIlikePattern(trimmed).replace(/^%|%$/g, '').replace(/\s+/g, '%');
            const isNum = /^\d+$/.test(trimmed) || /^[+\-0-9]+$/.test(trimmed);
            q = isNum
                ? q.or(`name.ilike.%${sq}%,email.ilike.%${sq}%,role.ilike.%${sq}%,phone.ilike.%${sq}%,candidate_number.eq.${trimmed}`)
                : q.or(`name.ilike.%${sq}%,email.ilike.%${sq}%,role.ilike.%${sq}%,phone.ilike.%${sq}%`);
        }

        return q;
    };

    const applyTalentPoolOrder = (query: any) => {
        if (orderBy === 'candidate' || orderBy === 'name') {
            return query.order('name', { ascending });
        }
        if (orderBy === 'candidate_number') {
            return query.order('candidate_number', { ascending });
        }
        const orderCol = orderBy === 'status' || orderBy === 'created_at' ? 'added_at' : orderBy;
        return query.order(orderCol, { ascending });
    };

    const sortMergedRows = (rows: any[]) => {
        const dir = ascending ? 1 : -1;
        const col =
            orderBy === 'candidate' || orderBy === 'name'
                ? 'name'
                : orderBy === 'candidate_number'
                  ? 'candidate_number'
                  : orderBy === 'status'
                    ? 'status'
                    : 'added_at';
        return [...rows].sort((a, b) => {
            const av = a?.[col];
            const bv = b?.[col];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (col === 'added_at') {
                return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
            }
            if (col === 'candidate_number') {
                return (Number(av) - Number(bv)) * dir;
            }
            return String(av).localeCompare(String(bv), 'nl') * dir;
        });
    };

    const useChunkedClientScope =
        allowedClientIds &&
        allowedClientIds.length > CLIENT_ID_FILTER_CHUNK &&
        allowedCandidateIds.length === 0 &&
        !hasScopedCandidateIds &&
        !aiIdsRestricting;

    if (useChunkedClientScope) {
        const countTalentPoolClientChunk = async (chunkIds: string[]) => {
            let cq = supabase.from('candidates').select('id', { count: 'exact', head: true });
            cq = cq.in('client_id', chunkIds);
            cq = applyTalentPoolFilters(cq);
            const { count: chunkCount, error: countErr } = await cq;
            if (countErr) throw countErr;
            return chunkCount || 0;
        };

        const fetchSortKeysForClientChunk = async (chunkIds: string[]) => {
            const acc: any[] = [];
            let offset = 0;
            while (acc.length < TALENT_POOL_MERGE_CAP) {
                let q = supabase.from('candidates').select(TALENT_POOL_SORT_KEY_COLS);
                q = q.in('client_id', chunkIds);
                q = applyTalentPoolFilters(q);
                q = applyTalentPoolOrder(q);
                q = q.range(offset, offset + TALENT_POOL_ID_FETCH_STEP - 1);
                const { data, error } = await q;
                if (error) throw error;
                if (!data?.length) break;
                acc.push(...data);
                if (data.length < TALENT_POOL_ID_FETCH_STEP) break;
                offset += TALENT_POOL_ID_FETCH_STEP;
            }
            return acc;
        };

        let totalCount = 0;
        const sortRows: any[] = [];
        const seenSortIds = new Set<string>();

        for (const ids of chunkUuidList(allowedClientIds!, CLIENT_ID_FILTER_CHUNK)) {
            totalCount += await countTalentPoolClientChunk(ids);
            const chunkRows = await fetchSortKeysForClientChunk(ids);
            for (const row of chunkRows) {
                if (!seenSortIds.has(row.id)) {
                    seenSortIds.add(row.id);
                    sortRows.push(row);
                }
            }
            if (sortRows.length >= TALENT_POOL_MERGE_CAP) break;
        }

        const sorted = sortMergedRows(sortRows);
        const pageKeys = sorted.slice(from, from + limit);
        const pageIds = pageKeys.map((r: { id: string }) => r.id).filter(Boolean);

        let pageRows: any[] = [];
        if (pageIds.length > 0) {
            const { data: fullRows, error: fullErr } = await supabase
                .from('candidates')
                .select(CANDIDATE_LIST_COLS_LIGHT)
                .in('id', pageIds);
            if (fullErr) throw fullErr;
            const byId = new Map((fullRows || []).map((r: any) => [r.id, r]));
            pageRows = pageIds.map((id: string) => byId.get(id)).filter(Boolean);
        }

        return { data: pageRows.map(mapTalentPoolCandidateRow), count: totalCount };
    }

    let query = supabase.from('candidates').select(CANDIDATE_LIST_COLS_LIGHT, { count: 'exact' });

    if (allowedClientIds && allowedClientIds.length > 0) {
        if (allowedCandidateIds.length > 0 && allowedClientIds.length <= 10) {
            const cIdsString = `(${allowedClientIds.join(',')})`;
            const candIdsStr = `(${allowedCandidateIds.slice(0, 20).join(',')})`;
            query = query.or(`client_id.in.${cIdsString},id.in.${candIdsStr}`);
        } else {
            query = query.in('client_id', allowedClientIds);
        }
    }

    query = applyTalentPoolFilters(query);
    query = applyTalentPoolOrder(query);
    query = query.range(from, from + limit - 1);

    const { data, count, error } = await query;
    if (error) throw error;

    return { data: (data || []).map(mapTalentPoolCandidateRow), count: count || 0 };
    
  } catch (error) {
    console.error("supabase_get_paginated_talent_pool failed:", error);
    return { data: [], count: 0 };
  }
};

/**
 * Returns the distinct candidate IDs that the given opdrachtgever (= client portal
 * user's company) is allowed to see in their talent pool. We scope by applications
 * whose vacancy has opdrachtgever_id or client_id equal to the user's company.
 * Capped at ~3000 most-recently-touched applications for safety.
 */
export const supabase_get_portal_scoped_candidate_ids = async (
  opdrachtgeverId: string | null | undefined,
  portalPrivacy?: PortalPrivacyOptions,
): Promise<string[]> => {
  try {
    if (!opdrachtgeverId) return [];
    const pageSize = 1000;
    let from = 0;
    const ids = new Set<string>();

    while (from < 6000) {
      const { data, error } = await supabase
        .from('applications')
        .select('candidate_id, status, vacancies!inner(id, opdrachtgever_id, client_id, eindklant_id)')
        .or(`opdrachtgever_id.eq.${opdrachtgeverId},eindklant_id.eq.${opdrachtgeverId}`, { foreignTable: 'vacancies' })
        .order('updated_at', { ascending: false })
        .range(from, from + pageSize - 1);

      if (error) throw error;
      if (!data || data.length === 0) break;

      for (const r of data) {
        const st = (r as any)?.status as string | undefined;
        if (!isPortalTalentPoolAllowedApplicationStatus(st, portalPrivacy)) continue;
        if (r?.candidate_id) ids.add(r.candidate_id);
      }
      if (data.length < pageSize) break;
      from += pageSize;
    }

    return Array.from(ids);
  } catch (e) {
    console.error('supabase_get_portal_scoped_candidate_ids failed:', e);
    return [];
  }
};

export const supabase_search_talent_pool = async (clientId: string | undefined, searchQuery: string) => {
  try {
    let allowedClientIds: string[] | null = null;
    if (clientId) {
      allowedClientIds = await supabase_get_authorized_client_ids(clientId);
      if (allowedClientIds.length === 0) {
          console.warn("[supabase_search_talent_pool] Empty allowedClientIds resolved from dirty mock ID! Aborting query to prevent .in() syntax error.");
          return [];
      }
    }

    if (allowedClientIds && allowedClientIds.length === 0) {
        console.warn("[supabase_get_clients] Empty allowedClientIds! Aborting query to prevent .in() syntax error.");
        return [];
    }
    const CHUNK_SIZE = 20;
    const clientChunks: (string[] | null)[] = [];
    if (allowedClientIds && allowedClientIds.length > CHUNK_SIZE) {
        for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) clientChunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
    } else {
        clientChunks.push(allowedClientIds);
    }

    const dbPat = searchDbIlikePattern(searchQuery.trim()).replace(/"/g, '');
    const promises = clientChunks.map(async (chunkIds) => {
        let query = supabase
          .from('candidates')
          .select(`${CANDIDATE_LIST_COLS}`)
          .or(`name.ilike."${dbPat}",email.ilike."${dbPat}",role.ilike."${dbPat}"`)
          .order('added_at', { ascending: false })
          .limit(50);
        if (chunkIds) query = query.in('client_id', chunkIds);
        return query;
    });

    const resArray = await Promise.all(promises);
    let data = resArray.flatMap(r => r.data || []);
    const anyError = resArray.find(r => r.error);
    if (anyError) throw anyError.error;

    let mappedData = data.map((c: any) => ({
        id: c.id, 
        client_id: c.client_id,
        candidate_id: c.id,
        status: c.status,
        ai_parsed_data: c.ai_parsed_data,
        parsed_cv: c.ai_parsed_data, 
        last_contact_at: c.updated_at,
        candidate: { ...c }
    }));

    mappedData = mappedData.filter((m) =>
        searchTextIncludesAny(searchQuery, m.candidate?.name, m.candidate?.email, m.candidate?.role),
    );

    // Locally slice back down to 50 if chunks returned too many
    mappedData.sort((a, b) => new Date(b.candidate.added_at).getTime() - new Date(a.candidate.added_at).getTime());
    if (mappedData.length > 50) mappedData = mappedData.slice(0, 50);
    
    return mappedData || [];
  } catch (error: any) {
    if (error.name === 'AbortError' || error.message?.includes('aborted')) return [];
    console.warn("Talent pool search error:", error);
    return [];
  }
};


import { dispatchGlobalWebhook } from './webhookService';

export const supabase_save_candidate = async (candidateData: any, profileData: any, clientId: string) => {
  let candidateId: string;
  let isNewCandidate = false;

  // Auto-generate sequentially increasing Candidate Number locally for profile legacy
  if (!profileData.ai_parsed_data) profileData.ai_parsed_data = {};
  if (!profileData.ai_parsed_data.candidate_number) {
      const { count } = await supabase.from('candidates').select('*', { count: 'estimated', head: true }).eq('client_id', clientId);
      const seqNum = (count || 0) + 1;
      profileData.ai_parsed_data.candidate_number = String(seqNum).padStart(5, '0');
  }

  candidateData.client_id = clientId;
  if (profileData && profileData.ai_parsed_data) {
      candidateData.ai_parsed_data = profileData.ai_parsed_data;
  }
  const resolvedSource =
      normalizeCandidateSource(candidateData.source) ||
      normalizeCandidateSource(profileData?.ai_parsed_data?.source);
  if (resolvedSource) {
      candidateData.source = resolvedSource;
      if (profileData?.ai_parsed_data && typeof profileData.ai_parsed_data === 'object') {
          profileData.ai_parsed_data = { ...profileData.ai_parsed_data, source: resolvedSource };
      }
  }
  
  const { data: exist } = await supabase.from('candidates').select('id, candidate_number').eq('email', candidateData.email).maybeSingle();

  // Global candidate number auto-increment logic
  if (!candidateData.candidate_number && (!exist || !exist.candidate_number)) {
      try {
          const { data: maxObj } = await supabase
              .from('candidates')
              .select('candidate_number')
              .order('candidate_number', { ascending: false, nullsFirst: false })
              .limit(1)
              .maybeSingle();
          candidateData.candidate_number = maxObj?.candidate_number ? maxObj.candidate_number + 1 : 6152;
      } catch (err) {
          console.error("Failed to fetch max candidate_number, defaulting to null", err);
      }
  } else if (exist && exist.candidate_number) {
      // Don't overwrite an existing candidate's number!
      delete candidateData.candidate_number;
  }

  // AUTO-GENERATION: GenAI Semantic Vector Indexing
  try {
      if (candidateData.cv_summary || candidateData.original_cv_text || candidateData.experience) {
          const { generateEmbedding } = await import('./geminiService');
          let text = `Name: ${candidateData.name || 'Unknown'}\nExperience: ${candidateData.experience || ''} years\n`;
          if (candidateData.cv_summary) text += `Summary: ${candidateData.cv_summary}\n`;
          if (candidateData.original_cv_text) text += `Original CV Context: ${candidateData.original_cv_text.substring(0, 5000)}\n`;
          const embed = await generateEmbedding(text.substring(0, 9500));
          if (embed) {
              candidateData.embedding = embed;
              console.log("[Vector Sync] Successfully generated 768-D real-time Embedding for:", candidateData.name);
          }
      }
  } catch (e: any) {
      console.error("[Vector Sync] Failed real-time embedding generation:", e.message);
  }

  if (exist) {
    candidateId = exist.id;
    await supabase.from('candidates').update(candidateData).eq('id', candidateId);
  } else {
    const { data: newCand, error } = await supabase.from('candidates').insert([candidateData]).select().single();
    if (error) throw error;
    candidateId = newCand.id;
    isNewCandidate = true;
  }

  let finalProfileData = { ...profileData, client_id: clientId };
  if (finalProfileData.candidate_id) delete finalProfileData.candidate_id;
  const { error: profError } = await supabase.from('candidates').update(finalProfileData).eq('id', candidateId);

  if (profError) throw profError;
  
  if (isNewCandidate) {
      dispatchGlobalWebhook('candidate_created', { candidateId, clientId });
  }
  
  return candidateId;
};

export const supabase_update_candidate_full = async (profileId: string, candidateId: string, candidateUpdates: any, profileUpdates: any) => {
  const { error: cErr } = await supabase.from('candidates').update(candidateUpdates).eq('id', candidateId);
  if (cErr) throw cErr;
  const { error: pErr } = await supabase.from('candidates').update(profileUpdates).eq('id', profileId);
  if (pErr) throw pErr;
};

export const supabase_delete_candidate_profile = async (profileId: string) => {
  const { error } = await supabase.from('candidates').delete().eq('id', profileId);
  return !error;
};

export const generateCandidateSlug = (name: string, idOrNumber: string | number) => { const safeName = (name || 'kandidaat').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''); return `${safeName}-${idOrNumber}`.replace(/-$/, ''); };

export const supabase_get_candidate_profile_by_slug_id = async (slugId: string, clientId?: string) => {
    try {
      let q = supabase.from('candidates').select('id,name,email,phone,role,experience,status,tags,avatar_url,photo_url,added_at,updated_at,cv_summary,address_city,address_zip,address_country,source,employment_preference,skill_set,original_cv_text,candidate_number,linkedin_url,salary_range,availability,ai_parsed_data,client_id');
      if (slugId.length === 36) { q = q.eq('id', slugId); } else { q = q.eq('candidate_number', parseInt(slugId, 10)); }
      const { data: candidate } = await q.single();
  
      if (candidate) {
        return {
          id: candidate.id,
          real_profile_id: candidate.id,
          candidate_id: candidate.id,
          client_id: candidate.client_id,
          status: candidate.status,
          tags: candidate.tags || [],
          ai_parsed_data: candidate.ai_parsed_data || {},
          created_at: candidate.added_at,
          candidate: candidate
        };
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
};

export const supabase_get_candidate_profile_by_id = async (candidateId: string, clientId: string) => {
    try {
      const { data: candidate } = await supabase.from('candidates')
          .select('id,name,email,phone,role,experience,status,tags,avatar_url,photo_url,added_at,updated_at,cv_summary,address_city,address_zip,address_country,source,employment_preference,skill_set,original_cv_text,candidate_number,linkedin_url,salary_range,availability,ai_parsed_data,client_id')
          .eq('id', candidateId).single();
  
      if (candidate) {
        return {
          id: candidate.id,
          real_profile_id: candidate.id,
          candidate_id: candidate.id,
          client_id: candidate.client_id,
          status: candidate.status,
          tags: candidate.tags || [],
          ai_parsed_data: candidate.ai_parsed_data || {},
          created_at: candidate.added_at,
          candidate: candidate
        };
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
};



// Applications & Reviews
export const supabase_get_vacancy_applications = async (vacancyId: string) => {
  try {
    // Step 1: get the vacancy's bullhorn_id (if any) to find duplicates
    const { data: vac } = await supabase
      .from('vacancies')
      .select('id, bullhorn_id')
      .eq('id', vacancyId)
      .single();

    // Step 2: collect all vacancy IDs to query (includes duplicates with same bullhorn_id)
    let vacancyIds = [vacancyId];
    if (vac?.bullhorn_id) {
      const { data: dupes } = await supabase
        .from('vacancies')
        .select('id')
        .eq('bullhorn_id', vac.bullhorn_id);
      if (dupes && dupes.length > 0) {
        vacancyIds = [...new Set(dupes.map((d: any) => d.id as string))] as string[];

      }
    }

    // Step 3: fetch applications for all matching vacancy IDs
    const { data, error } = await supabase
      .from('applications')
      .select('*, vacancies(title, status, data, eindklant:clients!vacancies_eindklant_id_fkey(name), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(name)), candidates(id, name, email, phone, role, candidate_number, avatar_url, photo_url, ai_parsed_data), source_client:clients!source_client_id(name)')
      .in('vacancy_id', vacancyIds);

    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

/** Lichte select voor global pipeline / kanban (zonder zware nested joins). */
export const KANBAN_PIPELINE_APPLICATION_SELECT =
  '*, candidates(id, name, email, phone, role, candidate_number, avatar_url, photo_url)';

const KANBAN_VACANCY_ID_CHUNK = 80;
const KANBAN_FETCH_PARALLEL = 12;

/** Haal sollicitaties op voor veel vacatures tegelijk (parallel chunks i.p.v. sequentiële batches). */
export const supabase_fetch_kanban_applications_for_vacancies = async (
  vacancyIds: readonly string[],
): Promise<any[]> => {
  const ids = sanitizeVacancyIdsForInFilter(vacancyIds);
  if (!ids.length) return [];

  const runChunk = async (chunk: string[]) => {
    const { data, error } = await supabase
      .from('applications')
      .select(KANBAN_PIPELINE_APPLICATION_SELECT)
      .in('vacancy_id', chunk)
      .order('created_at', { ascending: false })
      .limit(6000);
    if (error) throw error;
    return data || [];
  };

  if (ids.length <= KANBAN_VACANCY_ID_CHUNK) {
    return runChunk(ids);
  }

  const chunks = chunkArray(ids, KANBAN_VACANCY_ID_CHUNK);
  const merged: any[] = [];
  for (let i = 0; i < chunks.length; i += KANBAN_FETCH_PARALLEL) {
    const batch = chunks.slice(i, i + KANBAN_FETCH_PARALLEL);
    const parts = await Promise.all(batch.map((c) => runChunk(c)));
    for (const part of parts) merged.push(...part);
  }
  return Array.from(new Map(merged.map((a: any) => [a.id, a])).values());
};


const WHATSAPP_VACANCY_EMBED = `vacancies(
          id,
          title,
          data,
          vacancy_number,
          opdrachtgever_id,
          eindklant_id,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`;

/** Meest recente sollicitatie per kandidaat — voor WhatsApp-shortcodes (vacature + opdrachtgever). */
export const supabase_get_whatsapp_context_for_candidates = async (
    candidateIds: string[]
): Promise<Record<string, CandidateWhatsAppVacancyContext>> => {
    const ids = [...new Set(candidateIds.map((id) => String(id || '').trim()).filter(Boolean))];
    if (ids.length === 0) return {};

    try {
        const { data, error } = await supabase
            .from('applications')
            .select(`candidate_id, updated_at, ${WHATSAPP_VACANCY_EMBED}`)
            .in('candidate_id', ids)
            .neq('status', 'Draft_CV')
            .order('updated_at', { ascending: false });

        if (error) {
            console.warn('[supabase_get_whatsapp_context_for_candidates]', error);
            return {};
        }

        const map: Record<string, CandidateWhatsAppVacancyContext> = {};
        for (const row of data || []) {
            const cid = String((row as { candidate_id?: string }).candidate_id || '');
            if (!cid || map[cid]) continue;
            const vac = (row as { vacancies?: unknown }).vacancies;
            map[cid] = {
                vacancyTitle: vacancyTitleFromVacancyRecord(vac),
                companyName: companyNameFromVacancyRecord(vac),
                vacancyData: vac,
            };
        }
        return map;
    } catch (e) {
        console.warn('[supabase_get_whatsapp_context_for_candidates]', e);
        return {};
    }
};

export const supabase_get_candidate_applications = async (candidateId: string) => {
  try {
    const { data, error } = await supabase
      .from('applications')
      .select(
        `*,
        vacancies(
          id,
          title,
          vacancy_number,
          opdrachtgever_id,
          eindklant_id,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`
      )
      .eq('candidate_id', candidateId);
    if (error) console.error("Error fetching candidate apps:", error);
    return data || [];
  } catch (e) {
    return [];
  }
};

const APPLICATION_STAGE_HISTORY_VACANCY_EMBED = `vacancies(
          id,
          title,
          vacancy_number,
          opdrachtgever_id,
          eindklant_id,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`;

export const supabase_get_candidate_application_stage_history = async (candidateId: string) => {
  try {
    const { data, error } = await supabase
      .from('application_stage_history')
      .select(
        `id, application_id, candidate_id, vacancy_id, stage, previous_stage, changed_by_name, source, occurred_at, metadata, ${APPLICATION_STAGE_HISTORY_VACANCY_EMBED}`
      )
      .eq('candidate_id', candidateId)
      .order('occurred_at', { ascending: false });
    if (error) {
      if (error.code === '42P01' || String(error.message || '').includes('application_stage_history')) {
        return [];
      }
      console.warn('[supabase_get_candidate_application_stage_history]', error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.warn('[supabase_get_candidate_application_stage_history]', e);
    return [];
  }
};

export const supabase_get_candidate_interviews = async (candidateId: string) => {
  try {
    const { data, error } = await supabase
      .from('interviews')
      .select(
        `*,
        vacancies(
          id,
          title,
          vacancy_number,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`
      )
      .eq('candidate_id', candidateId)
      .order('scheduled_at', { ascending: false });
    if (error) console.error('[supabase_get_candidate_interviews]', error);
    return data || [];
  } catch (e) {
    return [];
  }
};

export const supabase_get_candidate_placements_light = async (candidateId: string) => {
  try {
    const { data, error } = await supabase
      .from('placements')
      .select(
        `id, number, status, created_at, vacancy_id, end_client, data,
        placement_opdrachtgever:clients!placements_opdrachtgever_id_fkey(id, name, logo_url),
        placement_eindklant:clients!placements_eindklant_id_fkey(id, name, logo_url),
        vacancies(
          id,
          title,
          vacancy_number,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`
      )
      .eq('candidate_id', candidateId)
      .order('created_at', { ascending: false });
    if (error) console.error('[supabase_get_candidate_placements_light]', error);
    return data || [];
  } catch (e) {
    return [];
  }
};

export const supabase_submit_application = async (vacancyId: string, clientId: string, applicantData: any) => {
  // Intercept mock testing profiles to avoid Foreign Key constraint crashes
  const isUUID = (str: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str);
  
  if (!isUUID(vacancyId) || !isUUID(clientId)) {
    console.warn("Mock Submission Detected or Invalid UUID string. Bypassing Supabase Database insert.", { vacancyId, clientId });
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API delay
    return 'mock-candidate-' + Date.now();
  }

  // Upload CV if provided
  let cvUrl = null;
  if (applicantData.file) {
    try {
      // Upload to 'public' bucket under 'resumes' folder
      cvUrl = await supabase_upload_file(applicantData.file, 'public', 'resumes/');
    } catch (e) {
      console.warn("CV Upload failed", e);
    }
  }

  const applicantSource = resolveApplicantCandidateSource(applicantData.source);

  const candidatePayload = {
    name: `${applicantData.firstName} ${applicantData.lastName}`,
    email: applicantData.email,
    phone: applicantData.phone,
    linkedin_url: applicantData.linkedin,
    cv_summary: applicantData.cvSummary,
    source: applicantSource,
  };

  // Auto AI Match if we have basic data but no matchScore yet
  let currentMatchScore = applicantData.matchScore || 0;
  let currentMatchReason = applicantData.matchReason || '';
  let currentPros = applicantData.pros || [];
  let currentCons = applicantData.cons || [];

  if (currentMatchScore === 0) {
    try {
      const { data: vacData } = await supabase.from('vacancies').select('title, description').eq('id', vacancyId).single();
      if (vacData) {
        const analysisData = {
          name: `${applicantData.firstName} ${applicantData.lastName}`,
          role: applicantData.parsedData?.role || 'Kandidaat',
          summary: applicantData.parsedData?.summary || applicantData.cvSummary || '',
          skills: applicantData.parsedData?.skills || applicantData.parsedData?.tags || [],
          timeline: applicantData.parsedData?.timeline || []
        };
        const matchResult = await performRealMatch(analysisData, {
          title: vacData.title,
          description: vacData.description,
          data: {}
        });
        currentMatchScore = matchResult.score;
        currentMatchReason = matchResult.reason;
        currentPros = matchResult.pros;
        currentCons = matchResult.cons;
      }
    } catch (e) {
      console.warn("Auto AI Match failed during submission", e);
    }
  }

  const profilePayload = {
    status: 'Shortlist',
    tags: ['NEW'],
    ai_parsed_data: {
      ...(applicantData.parsedData || {}), // Use full parsed data if available
      source: applicantSource,
      matchScore: currentMatchScore,
      match_reason: currentMatchReason,
      strong_points: currentPros,
      weak_points: currentCons,
      country: applicantData.country,
      current_status: applicantData.currentStatus,
      culture_fit: applicantData.cultureFit,
      cv_url: cvUrl // Save CV URL here
    }
  };

  const candidateId = await supabase_save_candidate(candidatePayload, profilePayload, clientId);

  const { error } = await supabase.from('applications').insert([{
    vacancy_id: vacancyId,
    candidate_id: candidateId,
    motivation: applicantData.motivation,
    status: 'Shortlist',
    match_score: currentMatchScore,
    cv_summary: currentMatchScore > 0 ? `AI Match (${currentMatchScore}%): ${currentMatchReason}` : applicantData.cvSummary,
    cv_snapshot: cvUrl ? { url: cvUrl } : null // Try to save to snapshot if column supports it, otherwise it's just extra data
  }]);

  if (error) throw error;

  return candidateId;
};

/** Maak `placements`-rij aan als sollicitatie in plaatsings-status staat en er nog geen rij is. */
export const supabase_ensure_placement_for_application = async (appId: string): Promise<void> => {
    try {
        const { data: appData, error: selErr } = await supabase
            .from('applications')
            .select('id, status, candidate_id, vacancy_id, vacancy:vacancies(client_id, opdrachtgever_id, eindklant_id)')
            .eq('id', appId)
            .single();
        if (selErr || !appData) return;
        if (!applicationStatusImpliesPlacement(appData.status)) return;
        if (!appData.candidate_id || !appData.vacancy_id) return;

        const { data: existing } = await supabase
            .from('placements')
            .select('id')
            .eq('candidate_id', appData.candidate_id)
            .eq('vacancy_id', appData.vacancy_id)
            .limit(1)
            .maybeSingle();
        if (existing?.id) return;

        const vac = Array.isArray((appData as any).vacancy) ? (appData as any).vacancy[0] : (appData as any).vacancy;
        const placementClientId = vac?.client_id;
        if (!placementClientId) {
            console.warn('[supabase_ensure_placement_for_application] geen client_id op vacature', appId);
            return;
        }

        const number = await supabase_get_next_placement_number();
        const { error: insErr } = await supabase.from('placements').insert([
            {
                client_id: placementClientId,
                candidate_id: appData.candidate_id,
                vacancy_id: appData.vacancy_id,
                opdrachtgever_id: vac?.opdrachtgever_id ?? null,
                eindklant_id: vac?.eindklant_id ?? null,
                number,
                status: 'Nieuw',
                source: 'Werftools Pipeline',
                data: { source_application_id: appData.id },
            },
        ]);
        if (insErr) {
            console.error('[supabase_ensure_placement_for_application] insert', insErr);
            return;
        }
        dispatchGlobalWebhook('contract_created', {
            candidateId: appData.candidate_id,
            vacancyId: appData.vacancy_id,
            clientId: placementClientId,
        });
    } catch (e) {
        console.error('[supabase_ensure_placement_for_application]', e);
    }
};

export const supabase_update_application_status = async (appId: string, status: string) => {
  const { error } = await supabase
    .from('applications')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', appId);
  if (error) throw error;

  if (applicationStatusImpliesPlacement(status)) {
      await supabase_ensure_placement_for_application(appId);
  }
};

export const supabase_delete_application = async (id: string) => {
  const { error } = await supabase.from('applications').delete().eq('id', id);
  return !error;
};

export const supabase_invite_vacancy_reviewer = async (vacancyId: string, email: string, role: string) => {
  return true;
};

export const supabase_save_candidate_review = async (vacancyId: string, candidateId: string, reviewer: string, score: number, comment: string) => {
  const { error } = await supabase.from('candidate_reviews').insert([{
    vacancy_id: vacancyId,
    candidate_id: candidateId,
    reviewer_email: reviewer,
    score,
    comment
  }]);
  if (error) throw error;
};

// Duplicate function removed. Usage is now covered by the earlier definition.

// Files & Assets
export const supabase_upload_image = async (file: File) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `cv-images/${Math.random()}.${fileExt}`;

    // Use 'public' bucket which is standard for website assets
    const { error } = await supabase.storage.from('public').upload(fileName, file);
    if (error) throw error;

    const { data } = supabase.storage.from('public').getPublicUrl(fileName);
    return data.publicUrl;
  } catch (err: any) {
    console.error("Error in supabase_upload_image:", err);
    throw new Error(err.message || "Unknown error uploading image");
  }
};

/**
 * Upload an attachment to a 'todos' folder in the bucket
 * @param file The file object (image, pdf, etc.)
 * @returns {Promise<string>} The public URL of the uploaded image
 */
export const supabase_upload_todo_attachment = async (file: File): Promise<string> => {
  try {
    const fileName = `todos/${uuidv4()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const { error } = await supabase.storage.from('public').upload(fileName, file);
    if (error) throw error;
    
    const { data } = supabase.storage.from('public').getPublicUrl(fileName);
    return data.publicUrl;
  } catch (err: any) {
    console.error("Error in supabase_upload_todo_attachment:", err);
    throw new Error(err.message || "Unknown error uploading attachment");
  }
};

export const supabase_upload_file = async (file: File, bucket: string, path: string) => {
  // Sanitize filename: replace spaces, remove non-alphanumeric, keep extension
  const fileExt = file.name.split('.').pop() || 'tmp';
  const safeBaseName = file.name.replace(`.${fileExt}`, '').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
  
  let uploadFile = file;
  let finalExt = fileExt;
  if (file.type === 'image/webp' || file.type === 'image/avif' || fileExt.toLowerCase() === 'webp') {
      try {
          if (typeof window !== 'undefined' && 'createImageBitmap' in window) {
              const bitmap = await createImageBitmap(file);
              const canvas = document.createElement('canvas');
              canvas.width = bitmap.width;
              canvas.height = bitmap.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                  ctx.drawImage(bitmap, 0, 0);
                  const pngBlob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/png'));
                  if (pngBlob) {
                      uploadFile = new window.File([pngBlob], `${safeBaseName}.png`, { type: 'image/png' });
                      finalExt = 'png';
                  }
              }
          }
      } catch (conversionError) {
          console.warn("Failed to automatically convert WebP/AVIF to PNG", conversionError);
      }
  }

  const fileName = `${path}${Math.random().toString(36).substring(7)}_${safeBaseName}.${finalExt}`;
  
  const { error } = await supabase.storage.from(bucket).upload(fileName, uploadFile);
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
  return data.publicUrl;
};

// CVs & Documents
export const supabase_get_cv_templates = async (clientId?: string) => {
  try {
    let query = supabase.from('cv_design_templates').select('*');
    if (clientId) {
      query = query.or(`client_id.eq.${clientId},client_id.is.null`);
    } else {
      // If no clientId (Admin/Recruiter global view), fetch all.
      // Previously we only fetched globals, which hid user templates from admins.
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((t: any) => ({
      ...t,
      clientId: t.client_id,
      previewUrl: t.preview_url
    }));
  } catch (error) {
    return [];
  }
};

export const supabase_save_cv_template = async (template: any) => {
  // Map camelCase to snake_case for the database
  const payload: any = {
    name: template.name,
    design: template.design,
    structure: template.structure,
    decorations: template.decorations || [],
    client_id: template.clientId || template.client_id,
    preview_url: template.previewUrl || template.preview_url
  };

  // Only include ID if it's a valid ID (not undefined/null/empty/default)
  if (template.id && template.id !== 'default') {
    payload.id = template.id;
    payload.updated_at = new Date().toISOString();
  } else {
    // It's an insert, remove id to let DB generate it
    delete payload.id;
  }

  const { data, error } = await supabase.from('cv_design_templates').upsert(payload).select().single();
  if (error) throw error;

  // Return with camelCase
  return {
    ...data,
    clientId: data.client_id,
    previewUrl: data.preview_url
  };
};

export const supabase_delete_cv_template = async (id: string) => {
  const { error } = await supabase.from('cv_design_templates').delete().eq('id', id);
  if (error) throw error;
};

export const supabase_get_cv_versions = async (candidateId: string) => {
  try {
    const { data } = await supabase.from('applications').select('*').eq('candidate_id', candidateId).eq('status', 'Draft_CV').order('created_at', { ascending: false });
    return data || [];
  } catch (e) { return []; }
};

export const supabase_save_cv_version = async (candidateId: string, vacancyId: string | null, content: any, name: string, snapshot: any) => {
  console.log("Supabase Service: Saving CV Version...", { candidateId, name });
  const { data, error } = await supabase.from('applications').insert([{
    candidate_id: candidateId,
    vacancy_id: vacancyId || null,
    status: 'Draft_CV',
    cv_summary: name,
    motivation: JSON.stringify(content),
    cv_snapshot: snapshot
  }]).select();

  if (error) {
    console.error("Supabase Service: SAVE CV FAILED", error);
    return false;
  }
  console.log("Supabase Service: SAVE CV SUCCESS", data);
  return true;
};

export const supabase_update_cv_version = async (versionId: string, content: any, name: string, snapshot: any) => {
  console.log("Supabase Service: Updating CV Version...", { versionId, name });
  const { error } = await supabase.from('applications').update({
    cv_summary: name,
    motivation: JSON.stringify(content),
    cv_snapshot: snapshot,
    updated_at: new Date().toISOString()
  }).eq('id', versionId);

  if (error) {
    console.error("Supabase Service: UPDATE CV FAILED", error);
    return false;
  }
  console.log("Supabase Service: UPDATE CV SUCCESS");
  return true;
};

export const supabase_check_cv_version_name = async (candidateId: string, name: string, excludeId?: string) => {
  let query = supabase.from('applications').select('id').eq('candidate_id', candidateId).eq('cv_summary', name);
  if (excludeId) query = query.neq('id', excludeId);

  const { data, error } = await query;
  if (error) {
    console.error("Error checking CV name:", error);
    return false;
  }
  return data && data.length > 0;
};

// CVs & Documents
// CVs & Documents
export const supabase_get_documents = async (clientId: string, isTemplate = false, candidateId?: string) => {
  try {
    let query = supabase.from('documents').select('*').eq('client_id', clientId).eq('is_template', isTemplate);
    if (candidateId) query = query.eq('candidate_id', candidateId);
    const { data, error } = await query.order('updated_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.warn("Error fetching documents:", error);
    return [];
  }
};

export const supabase_get_document_files = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('document_files')
      .select('*')
      .eq('user_id', userId)
      .not('storage_path', 'ilike', '%cv_templates%')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

const CLIENT_APPLICATIONS_SELECT = `*,
  candidates(id, name, role, email, phone, status, photo_url, candidate_number),
  vacancies!inner(
    id,
    title,
    vacancy_number,
    client_id,
    opdrachtgever_id,
    eindklant_id,
    status,
    data,
    opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
    eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
  )`;

const chunkIds = (ids: string[], size = 80): string[][] => {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
};

export type ClientShortlistBundle = {
  applications: any[];
  interviews: any[];
  placements: any[];
  stageHistory: any[];
};

/** Sollicitaties + interviews + plaatsingen + fase-historiek voor CRM-bedrijfs-shortlist (zoals Talentpool). */
export const supabase_get_client_shortlist_bundle = async (clientId: string): Promise<ClientShortlistBundle> => {
  const empty: ClientShortlistBundle = { applications: [], interviews: [], placements: [], stageHistory: [] };
  const cid = String(clientId || '').trim();
  if (!cid) return empty;
  try {
    const { data: apps, error } = await supabase
      .from('applications')
      .select(CLIENT_APPLICATIONS_SELECT)
      .or(`client_id.eq.${cid},opdrachtgever_id.eq.${cid},eindklant_id.eq.${cid}`, { foreignTable: 'vacancies' })
      .neq('status', 'Draft_CV')
      .order('created_at', { ascending: false });
    if (error) throw error;
    const applications = apps || [];
    const vacancyIds = [...new Set(applications.map((a: any) => a.vacancy_id).filter(Boolean))] as string[];
    const appIds = applications.map((a: any) => a.id).filter(Boolean) as string[];

    const interviews: any[] = [];
    if (vacancyIds.length > 0) {
      for (const chunk of chunkIds(vacancyIds)) {
        const { data, error: intErr } = await supabase
          .from('interviews')
          .select(
            `*,
            candidates(id, name, role, email, photo_url, candidate_number),
            vacancies(
              id,
              title,
              vacancy_number,
              opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
              eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
            )`
          )
          .in('vacancy_id', chunk)
          .order('scheduled_at', { ascending: false });
        if (intErr) console.warn('[supabase_get_client_shortlist_bundle] interviews', intErr);
        else if (data?.length) interviews.push(...data);
      }
    }

    const placementOrParts = [`opdrachtgever_id.eq.${cid}`, `eindklant_id.eq.${cid}`];
    if (vacancyIds.length > 0) {
      for (const chunk of chunkIds(vacancyIds, 40)) {
        placementOrParts.push(`vacancy_id.in.(${chunk.join(',')})`);
      }
    }
    const placements: any[] = [];
    const { data: plData, error: plErr } = await supabase
      .from('placements')
      .select(
        `id, number, status, created_at, vacancy_id, candidate_id, end_client, data,
        candidate:candidates(id, name, photo_url, candidate_number),
        placement_opdrachtgever:clients!placements_opdrachtgever_id_fkey(id, name, logo_url),
        placement_eindklant:clients!placements_eindklant_id_fkey(id, name, logo_url),
        vacancies(
          id,
          title,
          vacancy_number,
          opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(id, name, logo_url),
          eindklant:clients!vacancies_eindklant_id_fkey(id, name, logo_url)
        )`
      )
      .or(placementOrParts.join(','))
      .order('created_at', { ascending: false });
    if (plErr) console.warn('[supabase_get_client_shortlist_bundle] placements', plErr);
    else if (plData?.length) placements.push(...plData);

    const stageHistory: any[] = [];
    if (appIds.length > 0) {
      for (const chunk of chunkIds(appIds)) {
        const { data: histData, error: histErr } = await supabase
          .from('application_stage_history')
          .select(
            `id, application_id, candidate_id, vacancy_id, stage, previous_stage, changed_by_name, source, occurred_at, metadata, ${APPLICATION_STAGE_HISTORY_VACANCY_EMBED}`
          )
          .in('application_id', chunk)
          .order('occurred_at', { ascending: false });
        if (histErr) {
          if (histErr.code === '42P01' || String(histErr.message || '').includes('application_stage_history')) break;
          console.warn('[supabase_get_client_shortlist_bundle] stageHistory', histErr);
        } else if (histData?.length) stageHistory.push(...histData);
      }
    }

    return { applications, interviews, placements, stageHistory };
  } catch (error) {
    console.error('Error fetching client shortlist bundle:', error);
    return empty;
  }
};

export const supabase_get_client_applications = async (clientId: string) => {
  const bundle = await supabase_get_client_shortlist_bundle(clientId);
  return bundle.applications;
};

export const supabase_get_pro_cv_templates = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('document_files')
      .select('*')
      .or(`user_id.eq.${userId},visibility.eq.company,shared_with_users.cs.{${userId}}`)
      .ilike('storage_path', '%cv_templates%')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

export const supabase_save_document = async (doc: any) => {
  console.log("Supabase Service: Attempting to save document...", JSON.stringify(doc, null, 2));

  // Explicitly check for user session to debug auth context
  // const { data: sessionData } = await supabase.auth.getSession();
  // console.log("Supabase Service: Current Session User:", sessionData.session?.user?.id);

  const { data, error } = await supabase.from('documents').upsert(doc).select().single();

  if (error) {
    console.error("Supabase Service: SAVE FAILED. Details:", JSON.stringify(error, null, 2));
    console.error("Supabase Service: Payload that failed:", JSON.stringify(doc, null, 2));
    throw error;
  }

  console.log("Supabase Service: SAVE SUCCESS. Returned data:", data);
  return data;
};

export const supabase_delete_document = async (id: string) => {
  const { error } = await supabase.from('documents').delete().eq('id', id);
  if (error) throw error;
};

// Automation
export const supabase_get_flows = async (clientId?: string) => {
  try {
    let query = supabase.from('automation_flows')
      .select(`
            *,
            automation_steps (
                id,
                type,
                label,
                config,
                step_order
            )
        `)
      .order('created_at', { ascending: false });

    if (clientId) {
      query = query.eq('client_id', clientId);
    }

    const { data } = await query;

    return (data || []).map(flow => ({
      id: flow.id,
      name: flow.name,
      trigger: flow.trigger_type,
      trigger_config: flow.trigger_config,
      status: flow.status,
      client_id: flow.client_id,
      tags: flow.tags || [],
      steps: (flow.automation_steps || [])
        .sort((a: any, b: any) => a.step_order - b.step_order)
        .map((s: any) => ({
          id: s.id,
          type: s.type,
          label: s.label,
          config: s.config,
          order_index: s.step_order // Convert for frontend consumption if still needed
        }))
    }));
  } catch (e) { return []; }
};

export const supabase_save_flow = async (flow: any, steps: any[]) => {
  const { data: savedFlow, error } = await supabase.from('automation_flows').upsert(flow).select().single();
  if (error) throw error;

  // Clear old steps
  await supabase.from('automation_steps').delete().eq('flow_id', savedFlow.id);

  // Format steps for DB
  const stepsWithId = steps.map((s, i) => {
    const payload: any = {
      flow_id: savedFlow.id,
      step_type: s.type,
      label: s.label,
      config: s.config,
      order_index: i
    };

    // ONLY include id if it exists AND is not a temporary ('0.xxx') ID.
    // If we pass id: undefined, Supabase tries to insert NULL and fails the NOT NULL DB constraint.
    if (s.id && !s.id.startsWith('0.')) {
      payload.id = s.id;
    }

    return payload;
  });

  if (stepsWithId.length > 0) {
    const { error: stepsError } = await supabase.from('automation_steps').insert(stepsWithId);
    if (stepsError) {
      console.error("Failed saving steps", JSON.stringify(stepsError, null, 2));
      console.error("Payload was:", JSON.stringify(stepsWithId, null, 2));
    }
  }

  return savedFlow;
};

export const supabase_delete_flow = async (id: string) => {
  const { error } = await supabase.from('automation_flows').delete().eq('id', id);
  if (error) throw error;
};

export const supabase_get_templates = async () => {
  try {
    const { data } = await supabase.from('automation_templates').select('*');
    return data || [];
  } catch (e) { return []; }
};

export const supabase_upsert_template = async (template: any) => {
  const payload = { ...template };
  if (!payload.id || payload.id === 'default' || payload.id.startsWith('0.')) {
    delete payload.id;
  }
  const { data, error } = await supabase.from('automation_templates').upsert(payload).select().single();
  if (error) throw error;
  return data;
};

export const supabase_delete_template = async (id: string) => {
  const { error } = await supabase.from('automation_templates').delete().eq('id', id);
  if (error) throw error;
};

// Automation Logs
export const supabase_get_automation_logs = async (clientId?: string, limit: number = 50) => {
  try {
    let query = supabase.from('automation_logs').select(`
      *,
      automation_flows!inner(name)
    `).order('created_at', { ascending: false }).limit(limit);

    // We assume automation_logs has client_id OR we join through flows
    // If table structure lacks client_id, we just fetch all available to the user via RLS

    const { data, error } = await query;
    if (error) {
      console.warn("Error fetching automation logs. Table might not exist yet:", error.message);
      return [];
    }

    return (data || []).map((log: any) => ({
      id: log.id,
      flowName: log.automation_flows?.name || 'Unknown Flow',
      triggerEvent: log.trigger_event || 'Unknown',
      timestamp: log.created_at,
      status: log.status, // 'success' | 'failed' | 'running'
      stepCount: log.steps_executed || 0,
      details: log.details || ''
    }));
  } catch (e) {
    return [];
  }
};

// Logs & Comms
export const supabase_get_candidate_communications = async (profileId: string) => {
    if (!profileId) return [];
    // Geen embed `profiles(name)` — die geeft in productie vaak 403 door RLS op profiles bij join.
    const { data, error } = await supabase
        .from('candidate_communications')
        .select('*')
        .eq('candidate_id', profileId)
        .order('created_at', { ascending: true });

    const rows = data || [];
    if (error) {
        console.error('Error fetching comms:', error);
    }

    const userIds = [...new Set((error ? [] : rows).map((r: any) => r.user_id).filter(Boolean))] as string[];
    const nameByUserId: Record<string, string> = {};
    if (userIds.length > 0) {
        const { data: profs } = await supabase.from('profiles').select('id, name').in('id', userIds);
        (profs || []).forEach((p: any) => {
            if (p?.id) nameByUserId[String(p.id)] = String(p.name || '').trim() || 'Collega';
        });
    }
    const enriched = (error ? [] : rows).map((r: any) => ({
        ...r,
        profiles: {
            name:
                r.user_id && nameByUserId[String(r.user_id)]
                    ? nameByUserId[String(r.user_id)]
                    : r.user_id
                      ? 'Collega'
                      : 'Systeem',
        },
    }));
    
    // Also fetch pending Outbox messages from the todos table so users can see scheduled Whatsapp/Email drafts
    let outboxItems: any[] = [];
    let targetCandidateId = profileId;
    const { data: profData } = await supabase.from('candidates').select('id').eq('id', profileId).maybeSingle();
    if (profData?.id) targetCandidateId = profData.id;
    
    if (targetCandidateId) {
        const { data: todosData } = await supabase.from('todos')
            .select('*')
            .eq('candidate_id', targetCandidateId)
            .ilike('title', '[OUTBOX]%')
            .order('created_at', { ascending: true });
            
        if (todosData && todosData.length > 0) {
            outboxItems = todosData
                .filter((t: any) => t.status !== 'Completed') // Completed ones should already be pushed natively into candidate_communications by the worker
                .map((t: any) => ({
                    id: t.id,
                    profile_id: profileId,
                    type: t.title.toLowerCase().includes('whatsapp') ? 'whatsapp' : 'email',
                    content: `⏳ [Concept / Wachtrij]\n${t.description}`,
                    created_at: t.created_at,
                    profiles: { name: 'Outbox Systeem' }, // Mocking the sender name
                    is_outbox: true
                }));
        }

        const { data: notesData } = await supabase.from('candidate_notes').select('*').eq('candidate_id', targetCandidateId);
        if (notesData && notesData.length > 0) {
            const mappedNotes = notesData.map((n: any) => ({
                id: n.id,
                profile_id: profileId,
                type: 'note',
                content: n.content,
                created_at: n.created_at,
                profiles: { name: n.author_name || 'Notitie Systeem' }
            }));
            outboxItems = [...outboxItems, ...mappedNotes];
        }
    }

    const combined = [...enriched, ...outboxItems].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    return combined;
};

export const supabase_save_candidate_communication = async (profileId: string, userId: string, content: string, type: string) => {
    // Dynamically resolve real_profile_id if a Candidate ID was passed to prevent FK violations
    let actualProfileId = profileId;
    const { data: cCheck } = await supabase.from('candidates').select('id').eq('id', profileId).limit(1).maybeSingle();
    if (cCheck) {
        actualProfileId = cCheck.id;
    }

    const req: any = {
        candidate_id: actualProfileId,
        content: content,
        type: type.toLowerCase(),
    };
    if (userId && userId !== 'system') {
        req.user_id = userId;
    } else {
        const { data: auth } = await supabase.auth.getUser();
        if (auth?.user?.id) req.user_id = auth.user.id;
    }
    const { data, error } = await supabase.from('candidate_communications').insert(req).select('*').single();
    if (error) { console.error("Error saving comm:", error); return null; }

    // Update candidate's last_contact_at automatically
    try {
        let targetCandidateId = actualProfileId;
        const { data: prof } = await supabase.from('candidates').select('id').eq('id', actualProfileId).maybeSingle();
        if (prof?.id) targetCandidateId = prof.id;
        
        if (targetCandidateId) {
            await supabase.from('candidates').update({ updated_at: new Date().toISOString() }).eq('id', targetCandidateId);
        }
    } catch (e) {
        console.error("Failed to update last_contact_at", e);
    }

    return data;
};

/** Merge `pipeline_meta` in `applications.cv_snapshot` (o.a. gesprek-datum na Teams-planning). */
export const supabase_merge_application_pipeline_meta = async (
    applicationId: string,
    patch: Record<string, unknown>,
): Promise<boolean> => {
    if (!applicationId || !patch || typeof patch !== 'object') return false;
    try {
        const { data: row, error } = await supabase.from('applications').select('cv_snapshot').eq('id', applicationId).single();
        if (error) throw error;
        const snap = (row?.cv_snapshot && typeof row.cv_snapshot === 'object') ? row.cv_snapshot : {};
        const next = {
            ...(snap as Record<string, unknown>),
            pipeline_meta: {
                ...(((snap as any)?.pipeline_meta as Record<string, unknown>) || {}),
                ...patch,
            },
        };
        const { error: upErr } = await supabase
            .from('applications')
            .update({ cv_snapshot: next, updated_at: new Date().toISOString() })
            .eq('id', applicationId);
        if (upErr) throw upErr;
        return true;
    } catch (e) {
        console.error('[supabase_merge_application_pipeline_meta]', e);
        return false;
    }
};

// Client Communications
export const supabase_get_client_communications = async (clientId: string) => {
    const { data, error } = await supabase.from('client_communications').select('*').eq('client_id', clientId).order('created_at', { ascending: true });
    // If table doesn't exist yet, we will handle it via SQL later
    if (error) { console.error("Error fetching client comms:", error); return []; }
    return data || [];
};

export const supabase_save_client_communication = async (clientId: string, userId: string, content: string, type: string) => {
    const req = {
        client_id: clientId,
        user_id: userId,
        content: content,
        type: type,
        status: 'sent'
    };
    const { data, error } = await supabase.from('client_communications').insert(req).select('*, profiles(name)').single();
    if (error) { console.error("Error saving client comm:", error); return null; }
    return data;
};

export const supabase_get_recent_communications_global = async (clientId?: string, userId?: string, limit: number = 20) => {
  try {
    let query = supabase
      .from('candidate_communications')
      .select(`
        *,
        profiles!inner(name, client_id),
        candidates!inner(name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (clientId) {
      query = query.eq('profiles.client_id', clientId);
    }
    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;
    if (error) {
      console.error("supabase_get_recent_communications_global error:", error);
      throw error;
    }
    return data || [];
  } catch (e) {
    console.error("Failed fetching global comms:", e);
    return [];
  }
};

export const supabase_log_interaction = async (candidateId: string, clientId: string, type: string, content: string) => {
  try {
    let query = supabase.from('candidates').select('id').eq('id', candidateId);
    if (clientId && clientId !== 'system') {
        query = query.eq('client_id', clientId);
    }
    const { data: profiles, error } = await query.limit(1);
    
    if (error) console.warn("Log interaction finding profile issue:", error);
    
    const profile = profiles && profiles.length > 0 ? profiles[0] : null;
    if (profile) {
      await supabase_save_candidate_communication(profile.id, 'system', content, type);
    }
  } catch (e) { console.error("Log error", e); }
};

// Profiles
export const supabase_create_profile = async (profileData: any) => {
  // Use upsert to handle cases where Auth trigger already created the profile row
  const { data, error } = await supabase.from('profiles').upsert([profileData]).select().single();
  if (error) {
    console.error("supabase_create_profile failed:", error);
    return null;
  }
  return data;
};

// Client Settings
export const supabase_update_client_settings = async (clientId: string, settings: any) => {
  const { data, error } = await supabase
    .from('clients')
    .update({ ai_settings: settings })
    .eq('id', clientId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const supabase_get_client_settings = async (clientId: string) => {
  if (!clientId || clientId === 'undefined') return null;
  const { data, error } = await supabase
    .from('clients')
    .select('ai_settings')
    .eq('id', clientId)
    .single();

  if (error) {
     console.warn("Could not fetch ai_settings for client:", error);
     return null;
  }
  return data?.ai_settings || null;
};

// Plan & Usage
export const supabase_log_usage_event = async (clientId: string, type: string, metadata: any = {}) => {
  try {
    const { error } = await supabase.from('usage_events').insert([{
      client_id: clientId,
      event_type: type,
      metadata
    }]);
    if (error) console.warn("Usage log failed:", error.message);
  } catch (e) { console.warn("Usage log exception:", e); }
};

export const supabase_update_candidate = async (candidateId: string, updates: any) => {
  try {
    const { data, error } = await supabase
      .from('candidates')
      .update(updates)
      .eq('id', candidateId)
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.warn("supabase_update_candidate failed:", error);
    return null;
  }
};

// --- Admin Deletions ---
export const supabase_admin_delete_company = async (clientId: string) => {
  const { error } = await supabase.rpc('admin_delete_company', { target_client_id: clientId });
  if (error) throw error;
};

export const supabase_admin_delete_profile = async (profileId: string) => {
  const { error } = await supabase.rpc('admin_delete_profile', { target_profile_id: profileId });
  if (error) throw error;
};

// --- Integrations ---
export const supabase_upsert_imported_candidates = async (clientId: string, bullhornCandidates: any[], onProgress?: (msg: string) => void, onError?: (msg: string) => void) => {
  try {
    const results = { imported: 0, errors: 0, mappings: [] as { bhId: any, candidateId: string, profileId: string }[] };
    const candidateMappings = await getBullhornEntityMappings(clientId, 'candidate');
    const chunks = chunkArray(bullhornCandidates, 50);
    
    for (let c = 0; c < chunks.length; c++) {
      const chunk = chunks[c];
      
      await Promise.all(chunk.map(async (rawBhData) => {
          try {
            // Transform Bullhorn nested arrays to standard Werftools timeline and education maps
            const bhData = { ...rawBhData };
            if (bhData.workHistories && bhData.workHistories.data) {
                bhData.timeline = bhData.workHistories.data.map((wh: any) => ({
                    company: wh.companyName || '',
                    role: wh.title || bhData.occupation || wh.companyName || '',
                    period: wh.startDate ? new Date(wh.startDate).getFullYear() + (wh.endDate ? ' - ' + new Date(wh.endDate).getFullYear() : ' - heden') : '',
                    description: wh.comments || ''
                }));
            }
            if (bhData.educations && bhData.educations.data) {
                bhData.education = bhData.educations.data.map((edu: any) => ({
                    school: edu.school || '',
                    degree: edu.degree || '',
                    year: edu.startDate ? new Date(edu.startDate).getFullYear() + (edu.endDate ? ' - ' + new Date(edu.endDate).getFullYear() : '') : ''
                }));
            }
            if (!bhData.summary && bhData.description && typeof bhData.description === 'string') {
                bhData.summary = bhData.description.replace(/<[^>]*>?/gm, ''); // Strip HTML if it's there as a fallback profile summary
            }
            if (!bhData.tags && bhData.skillSet && Array.isArray(bhData.skillSet)) {
                bhData.tags = bhData.skillSet;
            }

            let candidateId = null;
            let profileId = null;
            const emailToUse = bhData.email || `${bhData.id}@no-email.bullhorn`;
            const mappedNameParts = getMappedValues(bhData, candidateMappings, 'name')
              .map((v) => `${v}`.trim())
              .filter(Boolean);
            const mappedName = mappedNameParts.length > 0
              ? mappedNameParts.join(' ')
              : `${bhData.firstName || ''} ${bhData.lastName || ''}`.trim();
            const mappedPhone = getMappedValue(bhData, candidateMappings, 'phone');
            const mappedCity = getMappedValue(bhData, candidateMappings, 'address_city');
            const mappedZip = getMappedValue(bhData, candidateMappings, 'address_zip');
            const mappedCountry = getMappedValue(bhData, candidateMappings, 'address_country');
            const mappedSource = getMappedValue(bhData, candidateMappings, 'source');
            const mappedSkillSet = getMappedValue(bhData, candidateMappings, 'skill_set');
            const mappedCv = getMappedValue(bhData, candidateMappings, 'original_cv_text');
            const mappedCandidateNumber = getMappedValue(bhData, candidateMappings, 'candidate_number');
            
            const { data: existingCandidate } = await supabase
              .from('candidates')
              .select('id')
              .eq('email', emailToUse)
              .maybeSingle();
              
            if (existingCandidate) {
              candidateId = existingCandidate.id;
              // Update basic fields — omit candidate_number (unique constraint, set on insert only)
              const { error: updateErr } = await supabase.from('candidates').update({
                  name: mappedName || 'Unknown Candidate',
                  phone: mappedPhone ?? bhData.phone ?? bhData.mobile ?? null,
                  address_city: mappedCity ?? bhData.address?.city ?? null,
                  address_zip: mappedZip ?? bhData.address?.zip ?? null,
                  address_country: mappedCountry ?? bhData.address?.countryName ?? null,
                  source: mappedSource ?? bhData.source ?? null,
                  employment_preference: Array.isArray(bhData.employmentPreference) ? bhData.employmentPreference : null,
                  skill_set: Array.isArray(mappedSkillSet) ? mappedSkillSet : (Array.isArray(bhData.skillSet) ? bhData.skillSet : null),
                  original_cv_text: (typeof mappedCv === 'string' ? mappedCv : null) ?? bhData.description ?? null,
              }).eq('id', candidateId);
              // 409 = unique constraint on another field — safe to ignore during bulk sync
              if (updateErr && updateErr.code !== '23505') console.warn('Candidate update warning:', updateErr.message);
            }
            
             if (!candidateId) {
                 // Create new candidate — use upsert to safely handle race conditions
                 const { data: newCandidate, error: insertError } = await supabase
                   .from('candidates')
                   .upsert({
                      name: mappedName || 'Unknown Candidate',
                       email: emailToUse,
                      phone: mappedPhone ?? bhData.phone ?? bhData.mobile ?? null,
                      address_city: mappedCity ?? bhData.address?.city ?? null,
                      address_zip: mappedZip ?? bhData.address?.zip ?? null,
                      address_country: mappedCountry ?? bhData.address?.countryName ?? null,
                      source: mappedSource ?? bhData.source ?? null,
                       employment_preference: Array.isArray(bhData.employmentPreference) ? bhData.employmentPreference : null,
                      skill_set: Array.isArray(mappedSkillSet) ? mappedSkillSet : (Array.isArray(bhData.skillSet) ? bhData.skillSet : null),
                      original_cv_text: (typeof mappedCv === 'string' ? mappedCv : null) ?? bhData.description ?? null,
                      candidate_number: mappedCandidateNumber != null ? toIntOrUndefined(mappedCandidateNumber) : (bhData.id ? toIntOrUndefined(bhData.id) : undefined)
                   }, { onConflict: 'email', ignoreDuplicates: false })
                   .select('id')
                   .single();
                   
                 if (insertError) throw insertError;
                 candidateId = newCandidate.id;
            }

            // Get existing profile to prevent overwriting AI CV data
            const { data: existingProfile } = await supabase
               .from('candidates')
               .select('id, ai_parsed_data, status')
               .eq('id', candidateId)
               .maybeSingle();

            let mergedAiData: any = { ...bhData, importedFromMsg: 'Bullhorn Sync' };

            if (existingProfile && existingProfile.ai_parsed_data) {
                const oldData = existingProfile.ai_parsed_data;
                mergedAiData = {
                    ...oldData, // keep all original AI data
                    bullhornData: bhData, // store fresh bullhorn data safely inside
                    importedFromMsg: 'Bullhorn Sync (Merged)'
                };
                
                // Only inject Bullhorn timeline/education if the AI parse didn't find any
                if (!oldData.timeline || oldData.timeline.length === 0) mergedAiData.timeline = bhData.timeline;
                if (!oldData.education || oldData.education.length === 0) mergedAiData.education = bhData.education;
            }

            // Update the client profile
            const { data: profileData, error: profileError } = await supabase
               .from('candidates')
               .update({
                   client_id: clientId,
                   status: existingProfile?.status || 'Active', // Preserve current stage instead of resetting to Active
                   ai_parsed_data: mergedAiData
               })
               .eq('id', candidateId)
               .select('id')
               .single();
               
            if (profileError) {
                console.error("Upsert conflict error:", profileError)
                throw profileError;
            }
            
            if (profileData) {
                profileId = profileData.id;
                results.mappings.push({ bhId: bhData.id, candidateId, profileId });
            }
            
            results.imported++;
          } catch (err: any) {
            console.error("Failed to import single Bullhorn candidate:", rawBhData.id, err);
            results.errors++;
            if (onError) onError(`Bullhorn ID: ${rawBhData.id} - ${err.message || 'Onbekende Fout'}`);
          }
      }));
      
      const processed = Math.min((c + 1) * 50, bullhornCandidates.length);
      if (onProgress) onProgress(`Kandidaten opslaan in database: ${processed} van de ${bullhornCandidates.length} verwerkt...`);
      await delay(100);
    }
    return results;
  } catch (error) {
     console.error("supabase_upsert_imported_candidates failed:", error);
     throw error;
  }
};

export interface BullhornSyncLog {
  client_id: string;
  candidate_id?: string;
  bullhorn_candidate_id?: string;
  action: 'import_create' | 'import_update' | 'export_create' | 'export_update' | 'rollback';
  status: 'success' | 'error' | 'pending';
  previous_data?: any;
  new_data?: any;
  error_message?: string;
}

export const supabase_log_bullhorn_sync = async (logData: BullhornSyncLog) => {
  try {
    const { error } = await supabase.from('bullhorn_sync_logs').insert([logData]);
    if (error) console.error("Error logging Bullhorn sync:", error);
  } catch (err) {
    console.error("Exception logging Bullhorn sync:", err);
  }
};

export const supabase_get_bullhorn_sync_logs = async (clientId: string, limit: number = 100) => {
  try {
    const { data, error } = await supabase
      .from('bullhorn_sync_logs')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(limit);
      
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("Error fetching Bullhorn sync logs:", err);
    return [];
  }
};

// -----------------------------------------------------------------------------------------
// Bullhorn Deep Integration (Notes & Documents)
// -----------------------------------------------------------------------------------------

export const supabase_sync_bullhorn_notes = async (clientId: string, profileId: string, userId: string, notes: any[], candidateId?: string) => {
    if (!notes || notes.length === 0) return;
    try {
        // If candidateId is not provided, try to look it up from profileId
        let targetCandidateId = candidateId;
        if (!targetCandidateId) {
            const { data: prof } = await supabase.from('candidates').select('id').eq('id', profileId).maybeSingle();
            if (prof) targetCandidateId = prof.id;
            else targetCandidateId = profileId; // profileId is actually candidateId
        }
        
        if (!targetCandidateId) {
            console.error("Cannot sync notes: candidateId is missing.");
            return;
        }

        const { data: existing } = await supabase.from('candidate_notes').select('content, created_at').eq('candidate_id', targetCandidateId);
        
        const newInserts = [];
        for (const n of notes) {
            const dateStr = n.dateAdded ? new Date(n.dateAdded).toISOString() : new Date().toISOString();
            const personName = n.commentingPerson ? `${n.commentingPerson.firstName || ''} ${n.commentingPerson.lastName || ''}`.trim() : (n.author ? `${n.author.firstName || ''} ${n.author.lastName || ''}`.trim() : '');
            const cleanComments = (n.comments || '')
                .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                .replace(/<[^>]+>/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/&[a-zA-Z]+;/g, '')
                .trim();
            const content = `[BULLHORN] ${n.action || 'Notitie'} - ${personName}:\n${cleanComments}`;
            
            const isDuplicate = existing?.some(e => e.content === content || e.content.includes(n.comments || '---'));
            
            if (!isDuplicate) {
                newInserts.push({
                    candidate_id: targetCandidateId,
                    author_id: userId,
                    author_name: personName || 'Froukje Sync',
                    content: content,
                    created_at: dateStr,
                    updated_at: dateStr
                });
            }
        }
        
        if (newInserts.length > 0) {
            const { error } = await supabase.from('candidate_notes').insert(newInserts);
            if (error) console.error("Error inserting Bullhorn notes:", error);
        }
    } catch (e) {
        console.error("Failed to sync Bullhorn notes:", e);
    }
};

export const supabase_sync_bullhorn_documents = async (clientId: string, candidateId: string, filesArray: any[]) => {
    if (!filesArray || filesArray.length === 0) return;
    
    try {
        const { data: existingDocs } = await supabase.from('documents').select('title').eq('candidate_id', candidateId);
        
        for (const rawFile of filesArray) {
            const file = rawFile.File ? rawFile.File : rawFile;
            if (!file.fileContent) continue;
            
            const isDuplicate = existingDocs?.some(d => d.title === file.name);
            if (isDuplicate) continue;
            
            try {
                const byteString = window.atob(file.fileContent);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: file.contentType || 'application/octet-stream' });
                
                const safeName = (file.name || 'document').replace(/[^a-zA-Z0-9.\-_]/g, '_');
                const filePath = `${clientId}/candidates/${candidateId}/${Date.now()}_${safeName}`;
                
                const { data: uploadData, error: uploadErr } = await supabase.storage.from('documents').upload(filePath, blob, {
                    contentType: file.contentType || 'application/octet-stream'
                });
                
                if (uploadErr) {
                    console.error("Error uploading Bullhorn file to storage:", uploadErr);
                    continue;
                }
                
                const { data: publicData } = supabase.storage.from('documents').getPublicUrl(uploadData.path);

                await supabase_save_document({
                    id: crypto.randomUUID(),
                    client_id: clientId,
                    candidate_id: candidateId,
                    title: file.name,
                    file_url: publicData.publicUrl,
                    storage_path: uploadData.path,
                    content: '',
                    is_template: false
                });
                

            } catch (innerErr) {
                console.error("Error processing single Bullhorn file (likely base64 decode):", innerErr);
            }
        }
    } catch (e) {
        console.error("Failed to sync Bullhorn documents:", e);
    }
}

// -----------------------------------------------------------------------------------------
// Bullhorn Client & Contact Sync
// -----------------------------------------------------------------------------------------

export const supabase_get_bullhorn_clients = async (clientId: string) => {
    const { data, error } = await supabase.from('clients').select('id, name, bullhorn_id, client_type').eq('parent_client_id', clientId).not('bullhorn_id', 'is', null);
    if (error) { 
        if (!error.message?.includes('AbortError')) console.error("Error fetching mapped clients:", error); 
        return []; 
    }
    return data || [];
};

export const supabase_get_bullhorn_contacts = async (crmClientId: string) => {
    const { data, error } = await supabase.from('client_contacts').select('id, first_name, last_name, email, bullhorn_id').eq('linked_client_id', crmClientId);
    if (error) { 
        if (!error.message?.includes('AbortError')) console.error("Error fetching mapped contacts:", error); 
        return []; 
    }
    return data || [];
};

export const supabase_sync_bullhorn_clients = async (clientId: string, records: any[], onProgress?: (msg: string) => void) => {
    console.log(`[Bullhorn Sync] Starting sync for ${records?.length || 0} clients`);
    if (!records || records.length === 0) return { success: true };
    try {
        const { data: allClients, error: clientsError } = await supabase
            .from('clients')
            .select('id, name, bullhorn_id')
            .eq('parent_client_id', clientId);
            
        if (clientsError) {
            console.error(`[Bullhorn Sync] Error fetching existing clients:`, clientsError);
            throw clientsError;
        }
        
        const existingClients = allClients || [];
        const existingIds: string[] = existingClients.map(e => e.bullhorn_id).filter(Boolean);
        console.log(`[Bullhorn Sync] Found ${existingClients.length} total CRM clients, ${existingIds.length} already linked.`);
        
        const inserts = [];
        const updates: { id: string; bullhorn_id: string; bullhorn_data: any }[] = [];
        
        for (const r of records) {
            const bhId = r.id?.toString();
            
            const existingLinked = existingClients.find(c => c.bullhorn_id === bhId);
            if (existingLinked) {
                updates.push({
                    id: existingLinked.id,
                    bullhorn_id: bhId,
                    bullhorn_data: r
                });
                continue;
            }
            
            const nameMatch = existingClients.find(c => 
                !c.bullhorn_id && 
                c.name?.toLowerCase().trim() === (r.name || '').toLowerCase().trim()
            );
            
            if (nameMatch) {
                console.log(`[Bullhorn Sync] Name match found for ${r.name}! Linking to CRM ID: ${nameMatch.id}`);
                updates.push({
                    id: nameMatch.id,
                    bullhorn_id: bhId,
                    bullhorn_data: r
                });
                nameMatch.bullhorn_id = bhId;
                existingIds.push(bhId);
                continue;
            }
            
            inserts.push({
                id: crypto.randomUUID(),
                name: r.name || 'Onbekend',
                bullhorn_id: bhId,
                parent_client_id: clientId,
                client_type: 'Direct',
                address_city: r.billingAddress?.city || null,
                address_street: r.billingAddress?.address1 || null,
                address_zip: r.billingAddress?.zip || null,
                phone: r.phone || null,
                website: r.companyURL || null,
                created_at: new Date().toISOString(),
                bullhorn_data: r
            });
        }
        
        console.log(`[Bullhorn Sync] Prepared ${inserts.length} new records and ${updates.length} updates for linking`);

        if (updates.length > 0) {
            const updateChunks = chunkArray(updates, 25);
            let processed = 0;
            for (const chunk of updateChunks) {
                await Promise.all(chunk.map(u => 
                    supabase.from('clients')
                        .update({ 
                            bullhorn_id: u.bullhorn_id, 
                            bullhorn_data: u.bullhorn_data,
                            name: u.bullhorn_data.name || 'Onbekend',
                            address_city: u.bullhorn_data.billingAddress?.city || null,
                            address_street: u.bullhorn_data.billingAddress?.address1 || null,
                            address_zip: u.bullhorn_data.billingAddress?.zip || null,
                            phone: u.bullhorn_data.phone || null,
                            website: u.bullhorn_data.companyURL || null
                        })
                        .eq('id', u.id)
                ));
                processed += chunk.length;
                if (onProgress) onProgress(`Bedrijven updaten: ${processed} van ${updates.length} records...`);
                await delay(150);
            }
        }

        if (inserts.length > 0) {
            const insertChunks = chunkArray(inserts, 100);
            let processed = 0;
            for (const chunk of insertChunks) {
                const { error } = await supabase.from('clients').insert(chunk);
                if (error) {
                    console.error("[Bullhorn Sync] Insert failed:", error);
                    throw error;
                }
                processed += chunk.length;
                if (onProgress) onProgress(`Bedrijven toevoegen: ${processed} van ${inserts.length} records...`);
                await delay(200);
            }
        }
        return { success: true, count: inserts.length + updates.length };
    } catch(err: any) {
        console.error("Client sync error:", err);
        return { success: false, error: err.message };
    }
};

export const supabase_sync_bullhorn_contacts = async (tenantId: string, records: any[], onProgress?: (msg: string) => void) => {
    console.log(`[Bullhorn Sync] Starting contact sync for ${records?.length || 0} contacts`);
    if (!records || records.length === 0) return { success: true };
    try {
        const { data: allContacts, error: contactsError } = await supabase
            .from('client_contacts')
            .select('id, first_name, last_name, email, bullhorn_id')
            .eq('client_id', tenantId);
            
        if (contactsError) throw contactsError;
        
        const existingContacts = allContacts || [];
        const existingIds: string[] = existingContacts.map(e => e.bullhorn_id).filter(Boolean);
        console.log(`[Bullhorn Sync] Found ${existingContacts.length} CRM contacts for tenant, ${existingIds.length} already linked.`);
        
        // Fetch CRM clients so we can link contacts properly
        const { data: mappedClients } = await supabase.from('clients').select('id, bullhorn_id').or(`parent_client_id.eq.${tenantId},id.eq.${tenantId}`);
        
        const inserts: any[] = [];
        const updates: { id: string; bullhorn_id: string; bullhorn_data: any }[] = [];
        
        const seenCurrentBatchBhIds = new Set<string>();
        const seenCurrentBatchEmails = new Set<string>();
        
        for (const r of records) {
            const bhId = r.id?.toString();
            
            if (seenCurrentBatchBhIds.has(bhId)) continue; // skip duplicate in batch
            seenCurrentBatchBhIds.add(bhId);
            
            const existingLinked = existingContacts.find(c => c.bullhorn_id === bhId);
            if (existingLinked) {
                updates.push({
                    id: existingLinked.id,
                    bullhorn_id: bhId,
                    bullhorn_data: r
                });
                continue;
            }
            
            // Deduplicate by email if present, against existing DB AND current batch
            const em = r.email ? r.email.toLowerCase().trim() : null;
            if (em && seenCurrentBatchEmails.has(em)) continue;
            
            let extMatch = null;
            if (em) {
                extMatch = existingContacts.find(c => !c.bullhorn_id && c.email && c.email.toLowerCase().trim() === em);
            }
            
            if (!extMatch) {
                extMatch = existingContacts.find(c => 
                    !c.bullhorn_id && 
                    c.first_name?.toLowerCase().trim() === (r.firstName || '').toLowerCase().trim() &&
                    c.last_name?.toLowerCase().trim() === (r.lastName || '').toLowerCase().trim()
                );
            }
            
            if (extMatch) {
                console.log(`[Bullhorn Sync] Match found for contact ${r.firstName} ${r.lastName}! Linking to CRM ID: ${extMatch.id}`);
                updates.push({
                    id: extMatch.id,
                    bullhorn_id: bhId,
                    bullhorn_data: r
                });
                extMatch.bullhorn_id = bhId;
                existingIds.push(bhId);
                if (em) seenCurrentBatchEmails.add(em);
                continue;
            }
            
            if (em) seenCurrentBatchEmails.add(em);
            
            // Link to the proper Client
            const bhClientId = r.clientCorporation?.id ? String(r.clientCorporation.id).trim() : null;
            const mappedClient = mappedClients?.find(c => String(c.bullhorn_id).trim() === bhClientId);
            const finalLinkedId = mappedClient ? mappedClient.id : null;
            
            inserts.push({
                id: crypto.randomUUID(),
                client_id: tenantId,
                linked_client_id: finalLinkedId,
                bullhorn_id: bhId,
                first_name: r.firstName || '',
                last_name: r.lastName || 'Onbekend',
                email: r.email || null,
                phone: r.mobile || r.phone || null,
                status: r.status || 'Active',
                created_at: new Date().toISOString(),
                bullhorn_data: r
            });
        }
        
        console.log(`[Bullhorn Sync] Prepared ${inserts.length} new records and ${updates.length} updates for linking`);

        if (updates.length > 0) {
            const updateChunks = chunkArray(updates, 25);
            let processed = 0;
            for (const chunk of updateChunks) {
                await Promise.all(chunk.map(u => 
                    supabase.from('client_contacts')
                        .update({ 
                            bullhorn_id: u.bullhorn_id, 
                            bullhorn_data: u.bullhorn_data,
                            first_name: u.bullhorn_data.firstName || '',
                            last_name: u.bullhorn_data.lastName || 'Onbekend',
                            email: u.bullhorn_data.email || null,
                            phone: u.bullhorn_data.phone || u.bullhorn_data.mobile || null,
                            function_title: u.bullhorn_data.occupation || null
                        })
                        .eq('id', u.id)
                ));
                processed += chunk.length;
                if (onProgress) onProgress(`Contacten updaten: ${processed} van ${updates.length} records...`);
                await delay(150);
            }
        }

        if (inserts.length > 0) {
            const insertChunks = chunkArray(inserts, 100);
            let processed = 0;
            for (const chunk of insertChunks) {
                const { error } = await supabase.from('client_contacts').insert(chunk);
                if (error) {
                    console.error("[Bullhorn Sync] Insert failed for contacts:", error.message, error.details, error.hint);
                    throw new Error(`Insert failed: ${error.message} (${error.details})`);
                }
                processed += chunk.length;
                if (onProgress) onProgress(`Contacten toevoegen: ${processed} van ${inserts.length} records...`);
                await delay(200);
            }
        }
        return { success: true, count: inserts.length + updates.length };
    } catch(err: any) {
        console.error("Contact sync error:", err);
        return { success: false, error: err.message };
    }
};

export const supabase_sync_bullhorn_vacancies = async (tenantId: string, records: any[], mappedClients: any[], onProgress?: (msg: string) => void) => {
    console.log(`[Bullhorn Sync] Starting sync for ${records?.length || 0} JobOrders (Vacancies)`);
    if (!records || records.length === 0) return { success: true };
    try {
        const vacancyMappings = await getBullhornEntityMappings(tenantId, 'vacancy');
        if (onProgress) {
            onProgress(`Mapping actief: ${vacancyMappings.length} vacaturevelden gekoppeld.`);
        }
        const bhIds = records.map(r => String(r.id).trim()).filter(Boolean);
        
        // Query by bullhorn_id in chunks to bypass Supabase's 1000-row limit on client_id queries
        const existingVacs: any[] = [];
        for (let i = 0; i < bhIds.length; i += 100) {
            const chunk = bhIds.slice(i, i + 100);
            const { data, error } = await supabase
                .from('vacancies')
                .select('id, title, bullhorn_id, client_id, data')
                .in('bullhorn_id', chunk);
                
            if (error) throw error;
            if (data) existingVacs.push(...data);
        }
        
        
        const mappedClientIds = mappedClients.map(c => c.id).filter(Boolean);
        let existingContacts: any[] = [];
        if (mappedClientIds.length > 0) {
            const { data: ec } = await supabase
                .from('client_contacts')
                .select('id, bullhorn_id, linked_client_id')
                .eq('client_id', tenantId)
                .in('linked_client_id', mappedClientIds);
            existingContacts = ec || [];
        }
            
        const existingIds = existingVacs?.map(v => String(v.bullhorn_id).trim()) || [];
        const inserts: any[] = [];
        const updates: any[] = [];
        
        const newClientsToInsert: any[] = [];
        const seenNewClientIds = new Set<string>();

        const getLifecycleStatus = (bhStatus: string): 'Open' | 'Afgerond' => {
            const lower = (bhStatus || '').toLowerCase();
            if (lower === 'open' || lower.includes('open') || lower.includes('accepting') || lower.includes('active') || lower.includes('approved')) return 'Open';
            return 'Afgerond';
        };

        const isoOrNull = (raw: any): string | null => {
            if (raw == null || raw === '') return null;
            try {
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return null;
                return d.toISOString();
            } catch {
                return null;
            }
        };
        
        for (const r of records) {
            const bhId = String(r.id).trim();
            const mappedTitle = getMappedValue(r, vacancyMappings, 'title');
            const mappedDescription = getMappedValue(r, vacancyMappings, 'description');
            const mappedStatusRaw = getMappedValue(r, vacancyMappings, 'status');
            const mappedBhId = getMappedValue(r, vacancyMappings, 'bullhorn_id');
            const effectiveBhId = mappedBhId != null && `${mappedBhId}`.trim() !== '' ? String(mappedBhId).trim() : bhId;
            const bhClientId = r.clientCorporation?.id ? String(r.clientCorporation.id).trim() : null;
            let mappedClient = mappedClients.find(c => String(c.bullhorn_id).trim() === bhClientId);
            
            if (!mappedClient && bhClientId) {
                if (!seenNewClientIds.has(bhClientId)) {
                    seenNewClientIds.add(bhClientId);
                    const autoClient = {
                        id: crypto.randomUUID(),
                        parent_client_id: tenantId,
                        bullhorn_id: bhClientId,
                        name: r.clientCorporation?.name || `Bullhorn Bedrijf ${bhClientId}`,
                        client_type: 'Direct',
                        created_at: new Date().toISOString()
                    };
                    newClientsToInsert.push(autoClient);
                    mappedClients.push(autoClient);
                    mappedClient = autoClient;
                } else {
                    mappedClient = mappedClients.find(c => String(c.bullhorn_id).trim() === bhClientId);
                }
                
                if (!mappedClient) {
                    console.warn(`[Bullhorn Sync] Skipping JobOrder ${bhId} because its ClientCorporation ${bhClientId} could not be auto-created.`);
                    continue;
                }
            } else if (!mappedClient) {
                console.warn(`[Bullhorn Sync] Skipping JobOrder ${bhId} because it has no ClientCorporation attached.`);
                continue;
            }
            
            const bhContactId = r.clientContact?.id ? String(r.clientContact.id).trim() : null;
            const mappedContact = existingContacts?.find(c => String(c.bullhorn_id).trim() === bhContactId);
            
            // Check if we already have it
            const extMatch = existingVacs?.find(v => String(v.bullhorn_id).trim() === bhId);
            
            if (extMatch) {
                const mappedLifecycle = getLifecycleStatus(String(mappedStatusRaw ?? r.status ?? ''));
                const bhDateAddedIso = isoOrNull(r.dateAdded);
                updates.push({
                    id: extMatch.id,
                    bullhorn_id: effectiveBhId,
                    vacancy_number: toIntOrUndefined(effectiveBhId),
                    status: mappedLifecycle,
                    title: `${mappedTitle ?? r.title ?? extMatch.title ?? 'Untitled Job'}`,
                    description: `${mappedDescription ?? extMatch.description ?? r.description ?? ''}`,
                    opdrachtgever_id: mappedContact?.linked_client_id || mappedClient.id,
                    eindklant_id: mappedClient.id,
                    data: {
                        ...(extMatch.data || {}),
                        bullhorn: {
                            ...((extMatch.data as any)?.bullhorn || {}),
                            id: effectiveBhId,
                            status: (mappedStatusRaw ?? r.status ?? ((extMatch.data as any)?.bullhorn?.status ?? null)),
                            dateAdded: bhDateAddedIso || ((extMatch.data as any)?.bullhorn?.dateAdded ?? null),
                        },
                        duration: r.durationWeeks ? `${r.durationWeeks} weeks` : (extMatch.data as any)?.duration,
                        salary: r.salary ? { min: r.salary, max: r.salary, currency: 'EUR', period: r.salaryUnit || 'Monthly' } : (extMatch.data as any)?.salary,
                        contractType: r.employmentType || (extMatch.data as any)?.contractType,
                        clientName: (extMatch.data as any)?.clientName || mappedClient.name,
                        location: r.address?.city || (extMatch.data as any)?.location,
                        category: r.employmentType || (extMatch.data as any)?.category,
                        startDate: r.startDate ? new Date(r.startDate).toISOString().split('T')[0] : (extMatch.data as any)?.startDate,
                        closingDate: r.dateClosed ? new Date(r.dateClosed).toISOString().split('T')[0] : (extMatch.data as any)?.closingDate,
                        assigned_contact_id: mappedContact?.id || (extMatch.data as any)?.assigned_contact_id,
                        bullhorn_contact: r.clientContact,
                        lifecycleStatus: mappedLifecycle,
                        isClosed: false
                    }
                });
            } else {
                const mappedLifecycle = getLifecycleStatus(String(mappedStatusRaw ?? r.status ?? ''));
                const bhDateAddedIso = isoOrNull(r.dateAdded);
                const createdAt = bhDateAddedIso || new Date().toISOString();
                inserts.push({
                    id: crypto.randomUUID(),
                    title: `${mappedTitle ?? r.title ?? 'Untitled Job'}`,
                    client_id: tenantId,
                    vacancy_number: toIntOrUndefined(effectiveBhId),
                    status: mappedLifecycle,
                    description: `${mappedDescription ?? r.description ?? ''}`,
                    opdrachtgever_id: mappedContact?.linked_client_id || mappedClient.id,
                    eindklant_id: mappedClient.id,
                    responsibilities: {},
                    data: {
                        bullhorn: {
                            id: effectiveBhId,
                            status: (mappedStatusRaw ?? r.status ?? null),
                            dateAdded: bhDateAddedIso,
                        },
                        duration: r.durationWeeks ? `${r.durationWeeks} weeks` : undefined,
                        salary: r.salary ? { min: r.salary, max: r.salary, currency: 'EUR', period: r.salaryUnit || 'Monthly' } : undefined,
                        contractType: r.employmentType || undefined,
                        clientName: mappedClient.name,
                        location: r.address?.city || '',
                        publishedToSite: true, // Imported vacancies should default to active
                        category: r.employmentType || 'Algemeen',
                        startDate: r.startDate ? new Date(r.startDate).toISOString().split('T')[0] : undefined,
                        closingDate: r.dateClosed ? new Date(r.dateClosed).toISOString().split('T')[0] : undefined,
                        assigned_contact_id: mappedContact?.id,
                        bullhorn_contact: r.clientContact,
                        lifecycleStatus: getLifecycleStatus(r.status),
                        isClosed: false
                    },
                    bullhorn_id: effectiveBhId,
                    created_at: createdAt,
                    updated_at: new Date().toISOString()
                });
            }
        }
        
        console.log(`[Bullhorn Sync] Prepared ${inserts.length} new vacancies and ${updates.length} updates`);

        if (newClientsToInsert.length > 0) {
            console.log(`[Bullhorn Sync] Auto-creating ${newClientsToInsert.length} missing clients`);
            if (onProgress) onProgress(`[Automatisch] ${newClientsToInsert.length} onbekende bedrijven aanmaken...`);
            
            const clientChunks = chunkArray(newClientsToInsert, 50);
            for (const chunk of clientChunks) {
                const { error: insErr } = await supabase.from('clients').insert(chunk);
                if (insErr && insErr.code !== '23505') {
                    console.error("[Bullhorn Sync] Auto-create clients failed:", insErr);
                }
            }
        }

        if (updates.length > 0) {
            const updateChunks = chunkArray(updates, 15);
            let processed = 0;
            for (const chunk of updateChunks) {
                await Promise.all(chunk.map(u => 
                    supabase.from('vacancies')
                        .update({ bullhorn_id: u.bullhorn_id, vacancy_number: u.vacancy_number, status: u.status, title: u.title, description: u.description, data: u.data, updated_at: new Date().toISOString() })
                        .eq('id', u.id)
                ));
                processed += chunk.length;
                if (onProgress) onProgress(`Vacatures updaten: ${processed} van ${updates.length} records...`);
                await delay(200);
            }
        }

        if (inserts.length > 0) {
            // Deduplicate inserts locally to avoid DB unique constraint conflicts gracefully
            const uniqueInserts: any[] = [];
            const seenBhIds = new Set<string>();
            for (const ins of inserts) {
                const key = String(ins.bullhorn_id).trim();
                if (!seenBhIds.has(key)) {
                    seenBhIds.add(key);
                    uniqueInserts.push(ins);
                }
            }

            const insertChunks = chunkArray(uniqueInserts, 50);
            let processed = 0;
            for (const chunk of insertChunks) {
                const { error: insErr } = await supabase.from('vacancies').insert(chunk);
                if (insErr && insErr.code !== '23505') {
                    console.error("[Bullhorn Sync] Insert failed:", insErr);
                }
                processed += chunk.length;
                if (onProgress) onProgress(`Vacatures toevoegen: ${processed} van ${uniqueInserts.length} records...`);
                await delay(250);
            }
        }
        
        return { success: true, count: inserts.length + updates.length };
        
    } catch(err: any) {
        console.error("Vacancy sync error:", err);
        return { success: false, error: err.message };
    }
};

/**
 * Ultra-safe sync: ONLY writes `vacancies.data.bullhorn.dateAdded` for existing Bullhorn-linked vacancies.
 * - No inserts
 * - No changes to title/status/description/other data keys
 */
export const supabase_sync_bullhorn_vacancy_dateAdded_only = async (
    tenantId: string,
    records: any[],
    onProgress?: (msg: string) => void
) => {
    if (!records || records.length === 0) return { success: true, count: 0 };
    try {
        const bhIds = records.map((r) => String(r.id).trim()).filter(Boolean);
        const existing: Array<{ id: string; bullhorn_id: string; data: any }> = [];

        for (let i = 0; i < bhIds.length; i += 150) {
            const chunk = bhIds.slice(i, i + 150);
            const { data, error } = await supabase
                .from('vacancies')
                .select('id, bullhorn_id, data')
                .in('bullhorn_id', chunk);
            if (error) throw error;
            if (data) existing.push(...(data as any));
        }

        const byBhId = new Map(existing.map((v) => [String(v.bullhorn_id).trim(), v]));
        const isoOrNull = (raw: any): string | null => {
            if (raw == null || raw === '') return null;
            try {
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return null;
                return d.toISOString();
            } catch {
                return null;
            }
        };

        const updates: Array<{ id: string; data: any }> = [];
        for (const r of records) {
            const bhId = String(r.id).trim();
            const row = byBhId.get(bhId);
            if (!row) continue; // No insert here by design
            const bhDateAddedIso = isoOrNull(r.dateAdded);
            const prev = row.data || {};
            const next = {
                ...prev,
                bullhorn: {
                    ...(prev as any)?.bullhorn,
                    dateAdded: bhDateAddedIso,
                },
            };
            // If nothing changes, skip write
            if (((prev as any)?.bullhorn?.dateAdded ?? null) === (bhDateAddedIso ?? null)) continue;
            updates.push({ id: row.id, data: next });
        }

        if (updates.length === 0) return { success: true, count: 0 };

        const chunks = chunkArray(updates, 25);
        let processed = 0;
        for (const chunk of chunks) {
            await Promise.all(
                chunk.map((u) =>
                    supabase
                        .from('vacancies')
                        .update({ data: u.data, updated_at: new Date().toISOString() })
                        .eq('id', u.id)
                )
            );
            processed += chunk.length;
            if (onProgress) onProgress(`Bullhorn dateAdded bijwerken: ${processed} van ${updates.length}...`);
            await delay(120);
        }

        return { success: true, count: updates.length };
    } catch (err: any) {
        console.error('supabase_sync_bullhorn_vacancy_dateAdded_only error:', err);
        return { success: false, error: err.message };
    }
};

export const supabase_sync_bullhorn_job_submissions = async (tenantId: string, vacancyId: string, submissions: any[]) => {
    if (!submissions || submissions.length === 0) return { success: true, count: 0 };
    try {
        const candidates = submissions.map(s => s.candidate).filter(Boolean);
        
        // VEILIGHEIDSCHECK: we overschrijven NOOIT meer bestaande kandidaten tijdens een pipeline sync.
        // Dit voorkomt dat de rijke AI-parsed CV data van bulk syncs verloren gaat!
        const emails = candidates.map(c => c.email || `${c.id}@no-email.bullhorn`).filter(Boolean);
        let localCandidates: any[] = [];
        
        if (emails.length > 0) {
            const { data } = await supabase.from('candidates').select('id, email').in('email', emails);
            if (data) localCandidates = data;
        }

        // Als er kandidaten in de pipeline zitten die nog NIET in de CRM database staan, inserten we ze minimaal
        for (const cand of candidates) {
            const email = cand.email || `${cand.id}@no-email.bullhorn`;
            if (!localCandidates.some(lc => lc.email === email)) {
                try {
                    const { data: newCand } = await supabase.from('candidates').insert({
                        name: `${cand.firstName || ''} ${cand.lastName || ''}`.trim() || 'Onbekend',
                        email: email,
                        client_id: tenantId,
                        ai_parsed_data: { importedFromMsg: 'Bullhorn Pipeline Sync (Minimal)', bullhornData: cand },
                        candidate_number: cand.id ? parseInt(cand.id, 10) : undefined
                    }).select('id, email').single();
                    
                    if (newCand) {
                        localCandidates.push(newCand);
                    }
                } catch (e: any) {
                    // Mocht the email stiekem toch bestaan negeren we het veilig
                    console.warn("Kon pipeline kandidaat niet aanmaken:", e.message);
                }
            }
        }
        const inserts: any[] = [];

        for (const sub of submissions) {
            const candList = localCandidates.filter(c => c.email === (sub.candidate?.email || `${sub.candidate?.id}@no-email.bullhorn`));
            if (candList.length > 0) {
                const rawStatus = sub.status || 'New';
                const exactStatus = mapBullhornSubmissionStatusToLocalStage(rawStatus);
                inserts.push({
                    vacancy_id: vacancyId,
                    candidate_id: candList[0].id,
                    status: exactStatus,
                    created_at: sub.dateAdded ? new Date(sub.dateAdded).toISOString() : new Date().toISOString()
                });
            }
        }

        if (inserts.length > 0) {
            const { data: existingApps } = await supabase.from('applications').select('id, candidate_id, status').eq('vacancy_id', vacancyId);
            const existingAppsMap = new Map<string, any>((existingApps || []).map((a: any) => [a.candidate_id, a]));
            
            const newInserts: any[] = [];
            const appsToUpdate: {id: string, status: string}[] = [];

            for (const i of inserts) {
                const existing = existingAppsMap.get(i.candidate_id);
                if (existing) {
                    if (existing.status !== i.status) {
                        // User specifically requested: "dont overwrite" pipeline status
                        // This ensures we never overwrite manual drag-and-drop progress made in Werftools CRM
                        // appsToUpdate.push({ id: existing.id, status: i.status });
                    }
                } else {
                    newInserts.push(i);
                }
            }
            
            // Obsolete: Proactive Profile Cloning to candidate_client_profiles removed
            
            if (newInserts.length > 0) {
                const insertChunks = chunkArray(newInserts, 100);
                for (const chunk of insertChunks) {
                    const { error } = await supabase.from('applications').insert(chunk);
                    if (error) throw error;
                    await delay(100);
                }
            }

            if (appsToUpdate.length > 0) {
                const updateChunks = chunkArray(appsToUpdate, 25);
                for (const chunk of updateChunks) {
                    await Promise.all(chunk.map((app: {id: string, status: string}) => 
                         supabase.from('applications').update({ status: app.status }).eq('id', app.id)
                    ));
                    await delay(100);
                }
            }
        }
        
        return { success: true, count: inserts.length };
    } catch (err: any) {
        console.error("JobSubmission sync error:", err);
        return { success: false, error: err.message };
    }
};

export const supabase_sync_bullhorn_vacancy_files = async (vacancyId: string, files: any[]) => {
    if (!files || files.length === 0) return { success: true };
    try {
        const { data: vac, error: getErr } = await supabase.from('vacancies').select('data').eq('id', vacancyId).single();
        if (getErr) throw getErr;
        
        const existingData = vac.data || {};
        const mappedFiles = files.map(f => ({
            id: f.id,
            name: f.name || 'Naamloos bestand',
            contentType: f.contentType || 'application/octet-stream',
            dateAdded: f.dateAdded,
            description: f.description,
            source: 'bullhorn'
        }));
        
        await supabase.from('vacancies').update({
            data: { ...existingData, files: mappedFiles }
        }).eq('id', vacancyId);

        return { success: true, count: mappedFiles.length };
    } catch (err: any) {
        console.error("Vacancy files sync error:", err);
        return { success: false, error: err.message };
    }
};

// ============================================================================
// Sync History API
// ============================================================================

export interface BullhornSyncHistoryLog {
    client_id: string;
    user_id?: string;
    sync_type: string;
    status: 'SUCCESS' | 'PARTIAL_SUCCESS' | 'ERROR';
    total_processed: number;
    total_errors: number;
    started_at: string;
    completed_at: string;
    error_details: string[];
}

export const supabase_log_sync_history = async (logData: BullhornSyncHistoryLog) => {
    try {
        const { error } = await supabase.from('bullhorn_sync_history').insert([logData]);
        if (error) console.error("Error logging sync history:", error);
    } catch (err) {
        console.error("Exception logging sync history:", err);
    }
};

export const supabase_get_sync_history = async (clientId: string, limit: number = 50) => {
    try {
        const { data, error } = await supabase
            .from('bullhorn_sync_history')
            .select('*')
            .eq('client_id', clientId)
            .order('completed_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error("Error fetching sync history:", err);
        return [];
    }
};

/**
 * Queue a change to be sent to Bullhorn globally
 */
export const queueBullhornSync = async (
    clientId: string,
    entityType: 'Candidate' | 'Note' | 'JobSubmission' | 'WorkHistory' | 'Education',
    entityId: string,
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    payload: any
) => {
    try {
        const { error } = await supabase.from('bullhorn_sync_queue').insert({
            client_id: clientId,
            entity_type: entityType,
            entity_id: entityId,
            action: action,
            payload: payload
        });
        if (error) {
            console.error("Failed to queue Bullhorn sync:", error.message);
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
};

// ============================================================================
// CRM Contacts Methods
// ============================================================================

export const supabase_save_client_contact = async (contactData: any) => {
    try {
        const tenant = WERFTOOLS_DEFAULT_TENANT_CLIENT_ID;
        const row = { ...contactData };
        const cid = String(row.client_id || '').trim();
        const linked = String(row.linked_client_id || '').trim();
        let org = linked;
        if (!org && cid && cid !== tenant) org = cid;
        row.client_id = tenant;
        row.linked_client_id = org || null;

        if (row.id) {
            const { data, error } = await supabase
                .from('client_contacts')
                .update(row)
                .eq('id', row.id)
                .select()
                .single();
            if (error) throw error;
            return data;
        } else {
            const { data, error } = await supabase
                .from('client_contacts')
                .insert([row])
                .select()
                .single();
            if (error) throw error;
            return data;
        }
    } catch (err: any) {
        console.error("Error saving client contact:", err.message);
        throw err;
    }
};

export const supabase_delete_client_contact = async (contactId: string) => {
    try {
        const { error } = await supabase
            .from('client_contacts')
            .delete()
            .eq('id', contactId);
        if (error) throw new Error(error.message || 'Onbekende fout bij verwijderen');
        return true;
    } catch (err: any) {
        console.error("Error deleting client contact:", err.message);
        throw err;
    }
};


// === NEW GLOBAL HELPER ===
export const supabase_search_global_candidate_match = async (parsedData: any) => {
    try {
        if (!parsedData) return null;
        const email = (parsedData.email || '').toLowerCase().trim();
        const phone = (parsedData.phone || '').replace(/[^0-9]/g, '');
        const name = (`${parsedData.firstName || ''} ${parsedData.lastName || ''}`).toLowerCase().trim();
        
        let query = supabase.from('candidates').select('*').limit(5);
        if (email) {
            query = query.ilike('email', `%${email}%`);
        } else if (phone && phone.length > 8) {
             query = query.like('phone', `%${phone.substring(phone.length - 8)}%`);
        } else if (name && name.length > 4) {
            query = query.ilike('name', `%${name}%`);
        } else {
            return null; // Not enough data
        }
        
        const { data, error } = await query;
        if (error || !data || data.length === 0) return null;
        return data[0] || null;
    } catch (e) {
        console.error('Error finding existing candidate', e);
        return null;
    }
};

// === MERGE CRM CLIENTS ===
export const supabase_merge_clients = async (sourceId: string, targetId: string) => {
    try {
        if (!sourceId || !targetId || sourceId === targetId) throw new Error("Invalid merge parameters");

        // 1. Move Vacancies (client_id, eindklant_id, opdrachtgever_id)
        await supabase.from('vacancies').update({ client_id: targetId }).eq('client_id', sourceId);
        await supabase.from('vacancies').update({ eindklant_id: targetId }).eq('eindklant_id', sourceId);
        await supabase.from('vacancies').update({ opdrachtgever_id: targetId }).eq('opdrachtgever_id', sourceId);

        // 2. Move Profiles & Users
        await supabase.from('profiles').update({ client_id: targetId }).eq('client_id', sourceId);

        // 3. Move Communications & Notes
        await supabase.from('client_communications').update({ client_id: targetId }).eq('client_id', sourceId);
        await supabase.from('client_contacts').update({ linked_client_id: targetId }).eq('linked_client_id', sourceId);
        await supabase
            .from('client_contacts')
            .update({ linked_client_id: targetId, client_id: WERFTOOLS_DEFAULT_TENANT_CLIENT_ID })
            .eq('client_id', sourceId);

        // 4. Move Todos
        await supabase.from('todos').update({ client_id: targetId }).eq('client_id', sourceId);

        // 5. Move Automation Settings
        await supabase.from('automation_flows').update({ client_id: targetId }).eq('client_id', sourceId);
        await supabase.from('automation_templates').update({ client_id: targetId }).eq('client_id', sourceId);

        // 6. Move Web Configs
        await supabase.from('website_configs').update({ client_id: targetId }).eq('client_id', sourceId);

        // 7. Delete Source Company
        const { error } = await supabase.from('clients').delete().eq('id', sourceId);
        if (error) throw error;
        
        return true;
    } catch (err: any) {
        console.error("Error merging clients:", err.message);
        throw err;
    }
};



/** True when the browser never got an HTTP response (offline, DNS, wrong URL, blocked request, or race during Supabase auth refresh). */
function supabaseErrorLooksLikeNetworkFailure(error: any): boolean {
    const msg = String(error?.message ?? '');
    return (
        msg.includes('Failed to fetch') ||
        msg.includes('NetworkError') ||
        msg.includes('Network request failed') ||
        msg.includes('Load failed') ||
        msg.includes('The Internet connection appears to be offline')
    );
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Lichte joins voor plaatsingen-lijst (geen candidates(*)/vacancies(*) — te zwaar voor grote tenants). */
const PLACEMENT_LIST_ROW_FIELDS =
    'id,number,status,candidate_id,vacancy_id,client_id,eindklant_id,opdrachtgever_id,recruiter_id,start_date,end_date,created_at,updated_at,data,contract_signed';
const PLACEMENT_LIST_CANDIDATE_FIELDS =
    'id,name,email,phone,candidate_number,photo_url,status';
/** Vacancy zonder geneste clients/recruiter — namen komen van placement.eindklant/opdrachtgever. */
const PLACEMENT_LIST_VACANCY_FIELDS =
    'id,title,vacancy_number,bullhorn_id,client_id,eindklant_id,opdrachtgever_id,assigned_recruiter_id';
/** Zelfde als PLACEMENT_LIST_VACANCY_FIELDS (applications-sync). */
const PLACEMENT_APP_EMBED_VACANCY_FIELDS = PLACEMENT_LIST_VACANCY_FIELDS;
const PLACEMENT_LIST_RECRUITER_FIELDS = 'id,name,email';
const PLACEMENT_LIST_CLIENT_FIELDS = 'id,name,logo_url';
const PLACEMENT_LIST_PAGE_SIZE = 400;

/** Virtual list row id from applications: `app-{applicationUuid}`. */
const parseAppPlacementApplicationId = (placementId: unknown): string | null => {
    if (typeof placementId !== 'string' || !placementId.startsWith('app-')) return null;
    const appUuid = placementId.slice(4).trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(appUuid) ? appUuid : null;
};

const placementCandidateVacancyKey = (candidateId: unknown, vacancyId: unknown): string => {
    const c = candidateId != null ? String(candidateId).trim() : '';
    const v = vacancyId != null ? String(vacancyId).trim() : '';
    return c && v ? `${c}::${v}` : '';
};

/** Hide app-* rows when a real placement already exists for the same kandidaat/vacature. */
const dedupeAppPlacementsAgainstReal = (rows: any[]): any[] => {
    const reals = rows.filter((p) => !String(p.id).startsWith('app-'));
    const pairKeys = new Set(
        reals.map((p) => placementCandidateVacancyKey(p.candidate_id, p.vacancy_id)).filter(Boolean)
    );
    const linkedAppIds = new Set(
        reals
            .map((p) => p.data?.source_application_id)
            .filter(Boolean)
            .map((id: unknown) => String(id).trim())
    );
    return rows.filter((p) => {
        if (!String(p.id).startsWith('app-')) return true;
        const appId = parseAppPlacementApplicationId(p.id);
        if (appId && linkedAppIds.has(appId)) return false;
        const key = placementCandidateVacancyKey(p.candidate_id, p.vacancy_id);
        if (key && pairKeys.has(key)) return false;
        return true;
    });
};

/** Keep newest real placement per kandidaat+vacature (voorkomt dubbele rijen na herhaald opslaan). */
const dedupeDuplicateRealPlacements = (rows: any[]): any[] => {
    const apps = rows.filter((p) => String(p.id).startsWith('app-'));
    const reals = rows.filter((p) => !String(p.id).startsWith('app-'));
    const bestByPair = new Map<string, any>();
    const unpaired: any[] = [];
    for (const p of reals) {
        const key = placementCandidateVacancyKey(p.candidate_id, p.vacancy_id);
        if (!key) {
            unpaired.push(p);
            continue;
        }
        const prev = bestByPair.get(key);
        if (!prev) {
            bestByPair.set(key, p);
            continue;
        }
        const prevTs = new Date(prev.updated_at || prev.created_at || 0).getTime();
        const curTs = new Date(p.updated_at || p.created_at || 0).getTime();
        if (curTs >= prevTs) bestByPair.set(key, p);
    }
    return [...apps, ...unpaired, ...bestByPair.values()];
};

export const supabase_get_placements = async (
    clientId?: string,
    connectedClients?: string[],
    /** Klantportaal: alleen rijen waar `eindklant_id` = ingelogde CRM-org (geen boom via parent_client_id). */
    portalEindklantScope?: boolean
) => {
    try {
        let allowedClientIds: string[] | null = null;
        if (clientId) {
            if (portalEindklantScope) {
                allowedClientIds = [clientId];
            } else {
                allowedClientIds = await supabase_get_authorized_client_ids(clientId);
                if (allowedClientIds.length === 0) return [];
            }
        }

        const CHUNK_SIZE = 8;
        let placementChunks: (string[] | null)[] = [];
        if (allowedClientIds && allowedClientIds.length > CHUNK_SIZE) {
            for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) {
                placementChunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
            }
        } else {
            placementChunks.push(allowedClientIds);
        }

        const placementChunkStats = { networkErrors: 0, otherErrors: 0 };

        const buildPlacementsQuery = (chunkIds: string[] | null) => {
            let query = supabase
                .from('placements')
                .select(
                    `${PLACEMENT_LIST_ROW_FIELDS}, candidate:candidates(${PLACEMENT_LIST_CANDIDATE_FIELDS}), vacancy:vacancies(${PLACEMENT_LIST_VACANCY_FIELDS}), recruiter:profiles(${PLACEMENT_LIST_RECRUITER_FIELDS}), eindklant:clients!placements_eindklant_id_fkey(${PLACEMENT_LIST_CLIENT_FIELDS}), opdrachtgever:clients!placements_opdrachtgever_id_fkey(${PLACEMENT_LIST_CLIENT_FIELDS})`
                )
                .order('created_at', { ascending: false });
            if (chunkIds && chunkIds.length > 0) {
                if (portalEindklantScope) {
                    query = query.in('eindklant_id', chunkIds);
                } else {
                    const idsString = `(${chunkIds.join(',')})`;
                    query = query.or(`client_id.in.${idsString},eindklant_id.in.${idsString},opdrachtgever_id.in.${idsString}`);
                }
            }
            return query;
        };

        const fetchPlacementsChunk = async (chunkIds: string[] | null) => {
            const acc: any[] = [];
            let offset = 0;
            while (true) {
                const run = () => buildPlacementsQuery(chunkIds).range(offset, offset + PLACEMENT_LIST_PAGE_SIZE - 1);
                let { data, error } = await run();
                if (error && supabaseErrorLooksLikeNetworkFailure(error)) {
                    await sleep(450);
                    ({ data, error } = await run());
                }
                if (error) {
                    if (supabaseErrorLooksLikeNetworkFailure(error)) placementChunkStats.networkErrors++;
                    else placementChunkStats.otherErrors++;
                    break;
                }
                if (!data?.length) break;
                acc.push(...data);
                if (data.length < PLACEMENT_LIST_PAGE_SIZE) break;
                offset += PLACEMENT_LIST_PAGE_SIZE;
            }
            return acc;
        };

        const placementResults: any[][] = [];
        for (const chunkIds of placementChunks) {
            placementResults.push(await fetchPlacementsChunk(chunkIds));
            if (placementChunks.length > 1) await sleep(80);
        }

        if (placementChunkStats.networkErrors > 0) {
            console.warn(
                `[supabase_get_placements] ${placementChunkStats.networkErrors} chunk(s) failed: network (Failed to fetch). Check internet, VITE_SUPABASE_URL, or retry after sign-in finishes.`
            );
        }
        if (placementChunkStats.otherErrors > 0) {
            console.warn(`[supabase_get_placements] ${placementChunkStats.otherErrors} chunk(s) failed (non-network).`);
        }
        const data = placementResults.flat();

        let appPlacements: any[] = [];
        try {
            const appSelect = `*, candidate:candidates(${PLACEMENT_LIST_CANDIDATE_FIELDS}), vacancy:vacancies(${PLACEMENT_APP_EMBED_VACANCY_FIELDS})`;

            const appPromises = placementChunks.map(async (chunkIds) => {
                if (chunkIds && chunkIds.length > 0) {
                    let vacData: { id: string }[] | null = null;
                    if (portalEindklantScope) {
                        const r = await supabase.from('vacancies').select('id').in('eindklant_id', chunkIds);
                        vacData = r.data;
                    } else {
                        const idsString = `(${chunkIds.join(',')})`;
                        const r = await supabase
                            .from('vacancies')
                            .select('id')
                            .or(`client_id.in.${idsString},eindklant_id.in.${idsString},opdrachtgever_id.in.${idsString}`);
                        vacData = r.data;
                    }
                    if (!vacData || vacData.length === 0) {
                        return [];
                    }
                    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                    const vacIds = [
                        ...new Set(
                            vacData
                                .map((v: any) => String(v?.id || '').trim())
                                .filter((id: string) => uuidRe.test(id))
                        ),
                    ];
                    if (vacIds.length === 0) return [];

                    /** GET-URL blijft onder PostgREST/proxy-limieten (lange select + veel UUIDs → 400). */
                    const APP_VACANCY_IN_CHUNK = 32;
                    const vacChunks = chunkArray(vacIds, APP_VACANCY_IN_CHUNK);

                    const chunkResults = await Promise.all(
                        vacChunks.map(async (vidChunk) => {
                            const { data: appsChunk, error: appErr } = await supabase
                                .from('applications')
                                .select(appSelect)
                                .in('status', ['Plaatsing', 'Aangenomen', 'Geplaatst', 'Placed'])
                                .in('vacancy_id', vidChunk);
                            if (appErr) {
                                console.warn(
                                    '[supabase_get_placements] applications chunk failed (vacancy_id.in):',
                                    appErr.message || appErr
                                );
                                return [] as any[];
                            }
                            return appsChunk || [];
                        })
                    );
                    const appsData = chunkResults.flat();

                    if (!appsData.length) return [];
                    return appsData.map((a) => ({
                        id: 'app-' + a.id,
                        number: 'APP-' + a.id.slice(0, 4).toUpperCase(),
                        status: 'actief',
                        start_date: a.created_at,
                        created_at: a.created_at,
                        candidate_id: a.candidate_id,
                        vacancy_id: a.vacancy_id,
                        client_id: a.vacancy?.client_id,
                        eindklant_id: a.vacancy?.eindklant_id,
                        opdrachtgever_id: a.vacancy?.opdrachtgever_id,
                        candidate: a.candidate,
                        vacancy: a.vacancy,
                        recruiter: null,
                        eindklant: a.vacancy?.eindklant,
                        opdrachtgever: a.vacancy?.opdrachtgever,
                        data: {
                            fees: {},
                            placementType: 'Werving & Selectie',
                        },
                    }));
                }

                const { data: appsData, error: appErr } = await supabase
                    .from('applications')
                    .select(appSelect)
                    .in('status', ['Plaatsing', 'Aangenomen', 'Geplaatst', 'Placed']);
                if (appErr) {
                    console.warn('[supabase_get_placements] applications (no client scope) failed:', appErr.message || appErr);
                    return [];
                }
                if (!appsData?.length) return [];
                return appsData.map((a) => ({
                    id: 'app-' + a.id,
                    number: 'APP-' + a.id.slice(0, 4).toUpperCase(),
                    status: 'actief',
                    start_date: a.created_at,
                    created_at: a.created_at,
                    candidate_id: a.candidate_id,
                    vacancy_id: a.vacancy_id,
                    client_id: a.vacancy?.client_id,
                    eindklant_id: a.vacancy?.eindklant_id,
                    opdrachtgever_id: a.vacancy?.opdrachtgever_id,
                    candidate: a.candidate,
                    vacancy: a.vacancy,
                    recruiter: null,
                    eindklant: a.vacancy?.eindklant,
                    opdrachtgever: a.vacancy?.opdrachtgever,
                    data: {
                        fees: {},
                        placementType: 'Werving & Selectie',
                    },
                }));
            });
            const appResults = await Promise.all(appPromises);
            appPlacements = appResults.flat();
        } catch (err) {
             console.error("Error fetching app placements", err);
        }

        const combinedData = [...(data || []), ...appPlacements];
        
        const uniqueIds = new Set();
        let uniqueData = combinedData.filter(p => {
             if (uniqueIds.has(p.id)) return false;
             uniqueIds.add(p.id);
             return true;
        });
        uniqueData = dedupeAppPlacementsAgainstReal(uniqueData);
        uniqueData = dedupeDuplicateRealPlacements(uniqueData);

        if (uniqueData && uniqueData.length > 0) {
            const clientIdsToFetch = new Set<string>();
            uniqueData.forEach(p => {
                if (p.data?.opdrachtgever_id) clientIdsToFetch.add(p.data.opdrachtgever_id);
                if (p.data?.eindklant_id) clientIdsToFetch.add(p.data.eindklant_id);
            });
            if (clientIdsToFetch.size > 0) {
                const allClientIds = Array.from(clientIdsToFetch);
                let clientMap = new Map();
                for (let i = 0; i < allClientIds.length; i += 100) {
                     const chunk = allClientIds.slice(i, i + 100);
                     const { data: clients } = await supabase.from('clients').select('id, name, logo_url').in('id', chunk);
                     if (clients) {
                         clients.forEach(c => clientMap.set(c.id, c));
                     }
                }
                
                uniqueData.forEach(p => {
                    const oId = p.data?.opdrachtgever_id;
                    const eId = p.data?.eindklant_id;
                    if (oId && clientMap.has(oId)) p.resolved_opdrachtgever = clientMap.get(oId);
                    if (eId && clientMap.has(eId)) p.resolved_eindklant = clientMap.get(eId);
                });
            }
        }
        
        return uniqueData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } catch (e) {
        console.error('Error fetching placements', e);
        return [];
    }
};

/** Lichte query: alleen plaatsingen voor één vacature (o.a. Clientportaal-tab). */
export const supabase_get_placements_for_vacancy = async (vacancyId: string): Promise<any[]> => {
    const vid = String(vacancyId || '').trim();
    if (!vid) return [];
    try {
        const { data, error } = await supabase
            .from('placements')
            .select(
                '*, candidate:candidates(*), vacancy:vacancies(*, client:clients!vacancies_client_id_fkey(*), eindklant:clients!vacancies_eindklant_id_fkey(*), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(*)), recruiter:profiles(*), eindklant:clients!placements_eindklant_id_fkey(*), opdrachtgever:clients!placements_opdrachtgever_id_fkey(*)'
            )
            .eq('vacancy_id', vid)
            .order('created_at', { ascending: false })
            .limit(80);
        if (error) {
            console.warn('[supabase_get_placements_for_vacancy]', error);
            return [];
        }
        return data || [];
    } catch (e) {
        console.warn('[supabase_get_placements_for_vacancy]', e);
        return [];
    }
};

/** Recruiter: werk `portal_client_proposal` + match_score bij zonder volledige publish-flow. */
export const supabase_recruiter_save_portal_proposal_snapshot = async (
    applicationId: string,
    payload: {
        matchScore?: number | null;
        summary?: string;
        matchNotes?: string;
        availableFrom?: string;
        hoursPerWeek?: string;
        salaryIndication?: string;
    }
): Promise<{ error: any | null }> => {
    const id = String(applicationId || '').trim();
    if (!id) return { error: new Error('Geen sollicitatie-id') };
    try {
        const { data: row, error: selErr } = await supabase.from('applications').select('cv_snapshot').eq('id', id).single();
        if (selErr) return { error: selErr };
        const raw = row?.cv_snapshot;
        const snap =
            raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
        const prevProp =
            snap.portal_client_proposal && typeof snap.portal_client_proposal === 'object' && !Array.isArray(snap.portal_client_proposal)
                ? { ...(snap.portal_client_proposal as Record<string, unknown>) }
                : {};
        const nextProp: Record<string, unknown> = { ...prevProp };
        if (payload.summary !== undefined) nextProp.summary = String(payload.summary || '');
        if (payload.matchNotes !== undefined) nextProp.match_notes = String(payload.matchNotes || '');
        if (payload.availableFrom !== undefined) nextProp.available_from = String(payload.availableFrom || '');
        if (payload.hoursPerWeek !== undefined) nextProp.hours_per_week = String(payload.hoursPerWeek || '');
        if (payload.salaryIndication !== undefined) nextProp.salary_indication = String(payload.salaryIndication || '');
        let roundedMatch: number | undefined;
        if (payload.matchScore !== undefined && payload.matchScore !== null && !Number.isNaN(Number(payload.matchScore))) {
            roundedMatch = Math.max(0, Math.min(100, Math.round(Number(payload.matchScore))));
            nextProp.match_score = roundedMatch;
        }
        snap.portal_client_proposal = nextProp;

        const updates: Record<string, unknown> = { cv_snapshot: snap, updated_at: new Date().toISOString() };
        if (roundedMatch !== undefined) {
            updates.match_score = roundedMatch;
        }
        const { error } = await supabase.from('applications').update(updates).eq('id', id);
        return { error: error || null };
    } catch (e) {
        return { error: e };
    }
};

// --- Client Engagement Center (klantportaal) ---------------------------------

const PORTAL_ENGAGEMENT_APPLICATION_SELECT = `
  id,
  status,
  match_score,
  created_at,
  updated_at,
  motivation,
  vacancy_id,
  candidate_id,
  cv_snapshot,
  vacancies(id, title, eindklant_id, opdrachtgever_id, assigned_recruiter_id),
  candidates(
    id,
    name,
    email,
    phone,
    role,
    photo_url,
    ai_parsed_data,
    original_cv_text,
    cv_summary,
    skill_set,
    salary_range,
    availability,
    employment_preference,
    linkedin_url
  )
`;

/** Vacature-IDs waar de portal-organisatie eindklant of opdrachtgever is. */
export const supabase_get_portal_engagement_vacancy_ids = async (clientId: string): Promise<string[]> => {
    const cid = String(clientId || '').trim();
    if (!cid) return [];
    try {
        const { data, error } = await supabase
            .from('vacancies')
            .select('id')
            .or(`eindklant_id.eq.${cid},opdrachtgever_id.eq.${cid}`);
        if (error) {
            console.warn('[supabase_get_portal_engagement_vacancy_ids]', error);
            return [];
        }
        return (data || []).map((r: { id: string }) => r.id);
    } catch (e) {
        console.warn('[supabase_get_portal_engagement_vacancy_ids]', e);
        return [];
    }
};

export const supabase_get_portal_engagement_applications = async (vacancyIds: string[]): Promise<any[]> => {
    if (!vacancyIds.length) return [];
    const chunkSize = 90;
    const out: any[] = [];
    try {
        for (let i = 0; i < vacancyIds.length; i += chunkSize) {
            const chunk = vacancyIds.slice(i, i + chunkSize);
            const { data, error } = await supabase
                .from('applications')
                .select(PORTAL_ENGAGEMENT_APPLICATION_SELECT)
                .in('vacancy_id', chunk)
                .order('updated_at', { ascending: false })
                .limit(500);
            if (error) {
                console.warn('[supabase_get_portal_engagement_applications]', error);
                continue;
            }
            if (data?.length) out.push(...data);
        }
        return out;
    } catch (e) {
        console.warn('[supabase_get_portal_engagement_applications]', e);
        return [];
    }
};

export const supabase_get_portal_engagement_interviews = async (vacancyIds: string[]): Promise<any[]> => {
    if (!vacancyIds.length) return [];
    const ids = vacancyIds.slice(0, 200);
    const fromIso = new Date(Date.now() - 14 * 86400000).toISOString();
    try {
        const { data, error } = await supabase
            .from('interviews')
            .select(
                `id, vacancy_id, candidate_id, scheduled_at, duration_minutes, location, video_link, status, stage_id,
         candidates(id, name, role, photo_url),
         vacancies(id, title, eindklant_id, opdrachtgever_id, assigned_recruiter_id)`
            )
            .in('vacancy_id', ids)
            .gte('scheduled_at', fromIso)
            .order('scheduled_at', { ascending: true })
            .limit(300);
        if (error) {
            console.warn('[supabase_get_portal_engagement_interviews]', error);
            return [];
        }
        return data || [];
    } catch (e) {
        console.warn('[supabase_get_portal_engagement_interviews]', e);
        return [];
    }
};

/** TODO: RLS / policies — clientportaal moet `applications` mogen updaten voor eigen vacatures. */
export const supabase_portal_update_application_status = async (
    applicationId: string,
    status: string,
    opts?: { comment?: string }
): Promise<{ error: any | null }> => {
    try {
        const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
        const c = opts?.comment?.trim();
        if (c) {
            const { data: row, error: selErr } = await supabase
                .from('applications')
                .select('cv_snapshot')
                .eq('id', applicationId)
                .single();
            if (!selErr && row) {
                const raw = row.cv_snapshot;
                const snap =
                    raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
                const at = new Date().toISOString();
                const prev = Array.isArray((snap as any).portal_client_feedback)
                    ? ([...(snap as any).portal_client_feedback] as unknown[])
                    : [];
                prev.push({ at, text: c });
                (snap as any).portal_client_feedback = prev;
                const threadPrev = Array.isArray((snap as any).portal_thread) ? ([...(snap as any).portal_thread] as unknown[]) : [];
                threadPrev.push({ at, author: 'client', text: c });
                (snap as any).portal_thread = threadPrev;
                updates.cv_snapshot = snap;
            }
        }
        const { error } = await supabase.from('applications').update(updates).eq('id', applicationId);
        if (error) return { error: error || null };

        if (applicationStatusImpliesPlacement(status)) {
            await supabase_ensure_placement_for_application(applicationId);
        }

        // Gerelateerde openstaande taken (o.a. klant-/voorgesteld-context) automatisch afronden zodat ze uit de TO DO-widget verdwijnen.
        try {
            const st = String(status || '');
            if (/afgewezen\s+door\s+opdrachtgever/i.test(st)) {
                const { data: appRow } = await supabase
                    .from('applications')
                    .select('vacancy_id, candidate_id')
                    .eq('id', applicationId)
                    .maybeSingle();
                const vid = appRow?.vacancy_id;
                const cid = appRow?.candidate_id;
                if (vid && cid) {
                    const { data: todoRows } = await supabase
                        .from('todos')
                        .select('id, title, description, is_external, status')
                        .eq('vacancy_id', vid)
                        .eq('candidate_id', cid);
                    const now = new Date().toISOString();
                    const ids = (todoRows || [])
                        .filter((t: any) => String(t.status || '').toLowerCase() !== 'completed')
                        .filter((t: any) => {
                            if (t.is_external === true) return true;
                            const blob = `${String(t.title || '')} ${String(t.description || '')}`.toLowerCase();
                            return /voorgesteld|voorstel|klantportaal|beoordeel|uitnodig/.test(blob);
                        })
                        .map((t: any) => t.id);
                    if (ids.length) {
                        await supabase
                            .from('todos')
                            .update({ status: 'Completed', completed_at: now })
                            .in('id', ids);
                    }
                }
            }
        } catch (e) {
            console.warn('[supabase_portal_update_application_status] todos auto-complete', e);
        }

        return { error: null };
    } catch (e) {
        return { error: e };
    }
};

/** Zet `applications.match_score` (o.a. klantportaal-kaart) bij Voorgesteld zonder portaal-publish. */
export const supabase_recruiter_apply_voorgesteld_match_score = async (
    applicationId: string,
    score: number | undefined | null
): Promise<{ error: any | null }> => {
    const id = String(applicationId || '').trim();
    if (!id) return { error: new Error('Geen sollicitatie-id') };
    if (score === undefined || score === null || Number.isNaN(Number(score))) return { error: null };
    const v = Math.max(0, Math.min(100, Math.round(Number(score))));
    try {
        const { error } = await supabase
            .from('applications')
            .update({ match_score: v, updated_at: new Date().toISOString() })
            .eq('id', id);
        return { error: error || null };
    } catch (e) {
        return { error: e };
    }
};

export type { VoorgesteldClientPayload } from '../utils/portalProposalThread';

/** Recruiter: publicatie van voorstelpakket in klantportaal (cv_snapshot.portal_client_proposal + thread). */
export const supabase_recruiter_publish_portal_proposal = async (
    applicationId: string,
    vacancyId: string,
    payload: VoorgesteldClientPayload | null | undefined,
    meta?: {
        recruiterName?: string;
        clientId?: string | null;
        candidateRealId?: string | null;
    }
): Promise<{ error: any | null }> => {
    if (!applicationId || !payload?.publishToPortal) return { error: null };
    const internalText = String(payload.clientProposalInternalNotes || '').trim();
    try {
        const { data: row, error: selErr } = await supabase
            .from('applications')
            .select('cv_snapshot')
            .eq('id', applicationId)
            .single();
        if (selErr) return { error: selErr };
        const raw = row?.cv_snapshot;
        const snap =
            raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
        const at = new Date().toISOString();
        (snap as any).portal_client_proposal = {
            summary: String(payload.recruiterSummary || '').trim(),
            available_from: String(payload.availableFrom || '').trim(),
            hours_per_week: String(payload.hoursPerWeek || '').trim(),
            salary_indication: String(payload.salaryIndication || '').trim(),
            match_notes: String(payload.matchNotes || '').trim(),
            match_score:
                typeof payload.portalMatchScore === 'number' && !Number.isNaN(payload.portalMatchScore)
                    ? Math.max(0, Math.min(100, Math.round(payload.portalMatchScore)))
                    : undefined,
            published_at: at,
            vacancy_id: String(vacancyId || '').trim(),
        };
        if (payload.portalCvSnapshot && typeof payload.portalCvSnapshot === 'object') {
            (snap as any).portal_client_cv_formatted = payload.portalCvSnapshot;
        }
        if (internalText) {
            const prevInt = Array.isArray((snap as any).recruiter_proposal_internal_notes)
                ? [...(snap as any).recruiter_proposal_internal_notes]
                : [];
            prevInt.push({ at, text: internalText, scope: 'voorgesteld' });
            (snap as any).recruiter_proposal_internal_notes = prevInt;
        }
        const threadPrev = Array.isArray((snap as any).portal_thread) ? [...(snap as any).portal_thread] : [];
        const parts: string[] = [];
        if (payload.recruiterSummary?.trim()) parts.push(`Samenvatting: ${payload.recruiterSummary.trim()}`);
        if (payload.matchNotes?.trim()) parts.push(`Match bij vacature: ${payload.matchNotes.trim()}`);
        if (payload.availableFrom?.trim()) parts.push(`Beschikbaar vanaf: ${payload.availableFrom.trim()}`);
        if (payload.hoursPerWeek?.trim()) parts.push(`Uren per week: ${payload.hoursPerWeek.trim()}`);
        if (payload.salaryIndication?.trim()) parts.push(`Salarisindicatie: ${payload.salaryIndication.trim()}`);
        const openLine = parts.length ? parts.join('\n') : 'Voorstel gedeeld via klantportaal.';
        threadPrev.push({ at, author: 'recruiter', text: openLine });
        (snap as any).portal_thread = threadPrev;
        const rowUpdates: Record<string, unknown> = {
            cv_snapshot: snap,
            updated_at: at,
        };
        if (typeof payload.portalMatchScore === 'number' && !Number.isNaN(payload.portalMatchScore)) {
            rowUpdates.match_score = Math.max(0, Math.min(100, Math.round(payload.portalMatchScore)));
        }
        const { error } = await supabase.from('applications').update(rowUpdates).eq('id', applicationId);
        if (error) return { error: error || null };

        if (internalText && vacancyId) {
            try {
                await supabase.from('vacancy_notes').insert({
                    vacancy_id: vacancyId,
                    author_name: String(meta?.recruiterName || 'Recruiter').trim() || 'Recruiter',
                    content: `[Voorgesteld — intern]\n${internalText}`,
                });
            } catch (e) {
                console.warn('[supabase_recruiter_publish_portal_proposal] vacancy_notes', e);
            }
            const cid = String(meta?.clientId || '').trim();
            const cand = String(meta?.candidateRealId || '').trim();
            if (cid && cand) {
                try {
                    await supabase_log_interaction(cand, cid, 'note', `[Voorgesteld — intern]\n${internalText}`);
                } catch (e) {
                    console.warn('[supabase_recruiter_publish_portal_proposal] log_interaction', e);
                }
            }
        }

        const candId = String(meta?.candidateRealId || '').trim();
        if (candId) {
            try {
                let vacancyTitle = '';
                if (vacancyId) {
                    const { data: vacRow } = await supabase
                        .from('vacancies')
                        .select('title')
                        .eq('id', vacancyId)
                        .maybeSingle();
                    vacancyTitle = String(vacRow?.title || '').trim();
                }
                const fullNote = buildVoorgesteldCandidateNoteContent(applicationId, vacancyTitle, payload);
                if (fullNote) {
                    const marker = voorgesteldCandidateNoteMarker(applicationId);
                    const { data: existingNotes } = await supabase
                        .from('candidate_notes')
                        .select('id, content')
                        .eq('candidate_id', candId)
                        .order('created_at', { ascending: false })
                        .limit(40);
                    const already = (existingNotes || []).some((n) =>
                        String(n.content || '').trimStart().startsWith(marker),
                    );
                    if (!already) {
                        const authorName = String(meta?.recruiterName || 'Recruiter').trim() || 'Recruiter';
                        const { data: auth } = await supabase.auth.getUser();
                        const authorId = auth?.user?.id;
                        if (authorId) {
                            await supabase.from('candidate_notes').insert({
                                candidate_id: candId,
                                vacancy_id: vacancyId || null,
                                author_id: authorId,
                                author_name: authorName,
                                content: fullNote,
                            });
                        } else {
                            console.warn(
                                '[supabase_recruiter_publish_portal_proposal] candidate_notes: geen ingelogde gebruiker',
                            );
                        }
                    }
                }
            } catch (e) {
                console.warn('[supabase_recruiter_publish_portal_proposal] candidate_notes', e);
            }
        }

        return { error: null };
    } catch (e) {
        return { error: e };
    }
};

/** Recruiter of klant: voeg puur bericht toe aan portal_thread (en bij klant ook legacy portal_client_feedback). */
export const supabase_append_portal_thread_message = async (
    applicationId: string,
    text: string,
    author: 'recruiter' | 'client'
): Promise<{ error: any | null }> => {
    const t = String(text || '').trim();
    if (!applicationId || !t) return { error: new Error('Leeg bericht') };
    try {
        const { data: row, error: selErr } = await supabase
            .from('applications')
            .select('cv_snapshot')
            .eq('id', applicationId)
            .single();
        if (selErr) return { error: selErr };
        const raw = row?.cv_snapshot;
        const snap =
            raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
        const at = new Date().toISOString();
        const threadPrev = Array.isArray((snap as any).portal_thread) ? [...(snap as any).portal_thread] : [];
        threadPrev.push({ at, author, text: t });
        (snap as any).portal_thread = threadPrev;
        if (author === 'client') {
            const prevFb = Array.isArray((snap as any).portal_client_feedback)
                ? [...(snap as any).portal_client_feedback]
                : [];
            prevFb.push({ at, text: t });
            (snap as any).portal_client_feedback = prevFb;
        }
        const { error } = await supabase
            .from('applications')
            .update({ cv_snapshot: snap, updated_at: at })
            .eq('id', applicationId);
        return { error: error || null };
    } catch (e) {
        return { error: e };
    }
};

/** Klantportaal: voeg notitie toe aan placements.data (JSONB). */
export const supabase_portal_append_placement_client_note = async (
    placementId: string,
    note: string
): Promise<{ error: any | null }> => {
    const text = String(note || '').trim();
    if (!text) return { error: new Error('Lege notitie') };
    try {
        const { data: row, error: selErr } = await supabase.from('placements').select('data').eq('id', placementId).single();
        if (selErr) return { error: selErr };
        const raw = row?.data;
        const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
        const prev = Array.isArray((data as any).client_portal_notes) ? ([...(data as any).client_portal_notes] as unknown[]) : [];
        prev.push({ at: new Date().toISOString(), text });
        (data as any).client_portal_notes = prev;
        const { error } = await supabase
            .from('placements')
            .update({ data, updated_at: new Date().toISOString() })
            .eq('id', placementId);
        return { error: error || null };
    } catch (e) {
        return { error: e };
    }
};

/** Klantportaal: markeer interview i.v.m. verplaatsing (korte status, past in varchar(20)). */
export const supabase_portal_request_interview_reschedule = async (
    interviewId: string
): Promise<{ error: any | null }> => {
    try {
        const { error } = await supabase
            .from('interviews')
            .update({ status: 'RescheduleReq' })
            .eq('id', interviewId);
        return { error: error || null };
    } catch (e) {
        return { error: e };
    }
};

export async function supabase_get_next_placement_number() {
    const { data } = await supabase.from('placements').select('number');
    let max = 0;
    if (data) {
        data.forEach(p => {
            if (p.number) {
                const numStr = p.number.replace(/[^0-9]/g, '');
                if (numStr) {
                    const num = parseInt(numStr, 10);
                    if (!isNaN(num) && num > max) max = num;
                }
            }
        });
    }
    return (max > 0 ? max + 1 : 1).toString();
}

export const supabase_save_placement = async (placement: any) => {
    try {
        const payload = { ...placement };
        // Remove joined relation objects
        delete payload.candidate;
        delete payload.vacancy;
        delete payload.recruiter;
        delete payload.resolved_opdrachtgever;
        delete payload.resolved_eindklant;
        delete payload.eindklant;
        delete payload.opdrachtgever;

        // `placements` has no `contract_type` column — store in JSONB `data` (legacy UI used top-level field).
        if (payload.contract_type !== undefined) {
            const baseData = payload.data && typeof payload.data === 'object' ? { ...payload.data } : {};
            if (payload.contract_type === '' || payload.contract_type == null) delete baseData.contract_type;
            else baseData.contract_type = payload.contract_type;
            payload.data = baseData;
        }
        delete payload.contract_type;

        // Persist FKs from nested `data` when the form only filled `data.*` (older UI paths).
        if (payload.data && typeof payload.data === 'object') {
            if (!payload.eindklant_id && payload.data.eindklant_id) payload.eindklant_id = payload.data.eindklant_id;
            if (!payload.opdrachtgever_id && payload.data.opdrachtgever_id) payload.opdrachtgever_id = payload.data.opdrachtgever_id;
        }

        // Fix empty dates causing invalid input syntax for type date
        if (payload.start_date === '') payload.start_date = null;
        if (payload.end_date === '') payload.end_date = null;

        if (!payload.number) {
            payload.number = await supabase_get_next_placement_number();
        }
        
        let sourceApplicationId: string | null = null;
        if (payload.id && typeof payload.id === 'string' && payload.id.startsWith('app-')) {
            sourceApplicationId = parseAppPlacementApplicationId(payload.id);
            delete payload.id;
        }
        if (sourceApplicationId) {
            const baseData = payload.data && typeof payload.data === 'object' ? { ...payload.data } : {};
            baseData.source_application_id = sourceApplicationId;
            payload.data = baseData;
        }

        // Eén plaatsing per kandidaat+vacature: update bestaande rij i.p.v. opnieuw inserten.
        if (!payload.id && payload.candidate_id && payload.vacancy_id) {
            const { data: existing } = await supabase
                .from('placements')
                .select('id')
                .eq('candidate_id', payload.candidate_id)
                .eq('vacancy_id', payload.vacancy_id)
                .order('updated_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (existing?.id) payload.id = existing.id;
        }

        if (payload.id) {
            const { data, error } = await supabase.from('placements').update(payload).eq('id', payload.id).select().single();
            if (error) throw error;
            return { success: true, data };
        } else {
            const { data, error } = await supabase.from('placements').insert(payload).select().single();
            if (error) throw error;
            return { success: true, data };
        }
    } catch (e: any) {
        console.error('Error saving placement', e);
        return { success: false, error: e.message };
    }
};

export const supabase_delete_placement = async (
    id: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const appId = parseAppPlacementApplicationId(id);
        if (appId) {
            // Geen rij in `placements` — haal sollicitatie uit plaatsings-statussen.
            const { error } = await supabase
                .from('applications')
                .update({ status: 'Actief', updated_at: new Date().toISOString() })
                .eq('id', appId);
            if (error) throw error;
            return { success: true };
        }
        const { error } = await supabase.from('placements').delete().eq('id', id);
        if (error) throw error;
        return { success: true };
    } catch (e: any) {
        console.error('Error deleting placement', e);
        return { success: false, error: e?.message || String(e) };
    }
};


export const supabase_sync_bullhorn_placements = async (clientId: string, placements: any[]) => {
    try {
        if (!placements || placements.length === 0) return { success: true, count: 0 };

        const { data: existingPlacements } = await supabase.from('placements').select('id, number').eq('client_id', clientId).like('number', 'BH-%');
        const existingMap = new Map(existingPlacements?.map(p => [p.number, p.id]) || []);

        const newPlacements: any[] = [];
        let updatedCount = 0;

        for (const p of placements) {
            const extractedStartDate = p.dateBegin || p.jobOrder?.startDate || p.dateAdded;
            const extractedEndDate = p.dateEnd || p.dateEffective;
            const extractedType = p.employmentType || p.jobOrder?.employmentType;
            const extractedEindklant = p.clientCorporation?.name;
            const extractedOpdrachtgever = p.jobOrder?.clientCorporation?.name || p.clientCorporation?.name;
            const extractedWerkplek = p.jobOrder?.address?.city || p.jobOrder?.location;

            let mappedStatus = 'Voorgedragen';
            const s = (p.status || '').toLowerCase();
            if (s.includes('approv') || s.includes('actief')) mappedStatus = 'Goedgekeurd';
            else if (s.includes('termin') || s.includes('eindig')) mappedStatus = 'Beëindigd';
            else if (s.includes('complet') || s.includes('voltooid')) mappedStatus = 'Voltooid';
            else if (s.includes('reject') || s.includes('afgewezen')) mappedStatus = 'Afgewezen';

            let extractedNotes = p.notes;
            if (p.notes && typeof p.notes === 'object' && p.notes.data && Array.isArray(p.notes.data)) {
                extractedNotes = p.notes.data.map((n: any) => {
                    const dateStr = n.dateAdded ? new Date(n.dateAdded).toLocaleDateString() : '';
                    const person = n.commentingPerson ? `${n.commentingPerson.firstName || ''} ${n.commentingPerson.lastName || ''}`.trim() : 'Onbekend';
                    return `[${dateStr}] ${person} - ${n.action || 'Notitie'}\n${n.comments || ''}`;
                }).join('\n\n');
            }

            const payload = {
                client_id: clientId,
                number: `BH-${p.id}`,
                status: mappedStatus,
                source: 'Bullhorn Sync',
                data: {
                    ...p,
                    opdrachtgever: extractedOpdrachtgever || p.opdrachtgever,
                    werkplek: extractedWerkplek || p.werkplek,
                    notes: extractedNotes
                },
                start_date: extractedStartDate ? new Date(extractedStartDate).toISOString() : null,
                end_date: extractedEndDate ? new Date(extractedEndDate).toISOString() : null,
                employment_type: extractedType,
                placement_type: extractedType,
                salary: p.salary,
                billing_rate: p.fee,
                end_client: extractedEindklant,
                contract_signed: true
            };

            const existingId = existingMap.get(`BH-${p.id}`);
            if (existingId) {
                // Remove id from payload so we don't accidentally update the PK
                await supabase.from('placements').update({
                    data: payload.data,
                    start_date: payload.start_date,
                    end_date: payload.end_date,
                    employment_type: payload.employment_type,
                    placement_type: payload.placement_type,
                    end_client: payload.end_client,
                    status: payload.status
                }).eq('id', existingId);
                updatedCount++;
            } else {
                newPlacements.push(payload);
            }
        }

        if (newPlacements.length > 0) {
            const { error } = await supabase.from('placements').insert(newPlacements);
            if (error) throw error;
        }
        
        return { success: true, count: newPlacements.length + updatedCount };
    } catch (e: any) {
        console.error('Error syncing bullhorn placements:', e);
        return { success: false, error: e.message };
    }
};

/** Import JobSubmission-geschiedenis uit Bullhorn naar `application_stage_history` (o.a. `changed_by_name`, `source: bullhorn`). Voer hiervoor eerst SQL uit: `sql/migration_application_stage_history_changed_by_name.sql`. */
export const supabase_backfill_bullhorn_application_stage_history = async (
    clientId: string,
    auth: { BhRestToken: string; restUrl: string },
    options?: {
        delayMs?: number;
        onProgress?: (msg: string) => void;
        onProgressEvent?: (ev: any) => void;
    }
) => {
    const { runBullhornApplicationStageHistoryBackfill } = await import('./bullhornApplicationHistoryBackfill');
    return runBullhornApplicationStageHistoryBackfill(supabase, clientId, auth, options);
};


